import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
    Space,
    Paper,
    SimpleGrid,
    Group,
    Title,
    Grid,
    TextInput,
    ActionIcon,
    Card,
    Button,
    Select,
    Text,
    Stack,
    Divider,
    Textarea,
    Box,
    Badge,
    Switch,
    TagsInput,
    Radio,
} from '@mantine/core';
import {
    IconArrowLeft,
    IconPlus,
    IconTrash,
    IconChevronDown,
    IconChevronRight,
    IconEdit,
    IconGripVertical,
    IconToggleLeft,
    IconList,
    IconFileText,
    IconCheck,
    IconExclamationCircle,
    IconEye,
    IconArrowUp,
    IconArrowDown,
    IconPhone,
} from '@tabler/icons-react';
import { modals } from '@mantine/modals';
import JsonView from 'react18-json-view';
import 'react18-json-view/src/style.css';
import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { showNotification } from '@mantine/notifications';
import { useFirestore } from '../../helpers/firebaseContext';
import { ConditionField } from './components/ConditionField';
import { TextFieldEditor } from './components/TextFieldEditorModal';
import BackgroundColorPickerDialog from './components/BackgroundColorPickerDialog';
import FormCardWrapper from '../../components/form_card_wrapper';
import SimpleRegexSelector from './components/SimpleRegexSelector';
import {
    PageData,
    PageAction,
    SectionField,
    FieldTypes,
    ComponentOption,
    FieldTypeConfig,
    ComponentSection,
    AgreementType,
} from '../../types';
import { DatePickerInput } from '@mantine/dates';
import { fromMantineDate } from '../../helpers/mantineDates';

// ==================== CONSTANTS ====================

/**
 * Background colors for different nesting levels
 * Provides visual hierarchy for nested components
 */
const NESTING_COLORS = {
    level0: 'white',
    level1: '#f8f9fa',
    level2: '#e9ecef',
    level3: '#dee2e6',
    option0: '#f8f9fa',
    option1: '#fff3cd',
    option2: '#d4edda',
    option3: '#f8d7da',
} as const;

/**
 * Border colors for nested fields based on level
 */
const BORDER_COLORS = {
    level1: '#007bff',
    level2: '#28a745',
    level3: '#ffc107',
    level4: '#dc3545',
} as const;

/**
 * Firestore collection paths
 */
const FIRESTORE_PATHS = {
    CONFIG: 'config',
    KYC_CONFIG: 'kyc_configuration',
    KYC_PAGES: 'kyc_pages',
} as const;

/**
 * Field types that should hide contract-specific controls
 */
const CONTRACT_CONTROL_HIDDEN_TYPES = new Set([
    FieldTypes.PACI,
    FieldTypes.SIGNATURE,
    FieldTypes.FILE_UPLOAD,
    FieldTypes.RADIO,
    FieldTypes.SELECT,
]);

const DEFAULT_RETURN_PATH = '/kyc_configuration?tab=kyc_pages';

const getSafeReturnPath = (returnTo: string | null): string => {
    const trimmedReturnTo = returnTo?.trim();

    if (!trimmedReturnTo || !trimmedReturnTo.startsWith('/') || trimmedReturnTo.startsWith('//')) {
        return DEFAULT_RETURN_PATH;
    }

    return trimmedReturnTo;
};

/**
 * Field types that support options (radio, checkbox, select)
 */
const OPTION_SUPPORTING_TYPES = new Set([
    FieldTypes.RADIO,
    FieldTypes.CHECKBOX,
    FieldTypes.SELECT,
]);

/**
 * Field types that support tooltip (radio, checkbox, text, select, signature)
 */
const TOOLTIP_SUPPORTING_TYPES = new Set([
    FieldTypes.RADIO,
    FieldTypes.CHECKBOX,
    FieldTypes.SELECT,
    FieldTypes.TEXT,
    FieldTypes.SIGNATURE,
]);

// ==================== FIELD TYPE CONFIGURATION ====================

/**
 * Configuration object defining all available field types and their properties.
 * Each field type includes metadata, default templates, and validation rules.
 */
const FIELD_TYPES_CONFIG: Record<string, FieldTypeConfig> = {
    text: {
        type: FieldTypes.TEXT,
        name: 'Text Display',
        name_ar: 'عرض نص',
        icon: IconFileText,
        hasOptions: false,
        hasPlaceholder: false,
        hasRegex: false,
        is_visible_kyc: true,
        is_visible_contract: true,
        defaultTemplate: {
            type: FieldTypes.TEXT,
            label: 'Enter text content',
            label_ar: 'أدخل النص',
            showPlaceholder: false,
            showTwoBox: false,
            required: false,
        },
    },
    input: {
        type: FieldTypes.INPUT,
        name: 'Text Input',
        name_ar: 'إدخال نص',
        icon: IconEdit,
        hasOptions: false,
        hasPlaceholder: true,
        hasRegex: true,
        is_visible_kyc: true,
        is_visible_contract: true,
        defaultTemplate: {
            type: FieldTypes.INPUT,
            label: 'Field label',
            label_ar: 'التسمية',
            placeholder: 'Enter placeholder',
            placeholder_ar: 'أدخل النص التوضيحي',
            key: 'field_key',
            required: true,
            showPlaceholder: false,
            showTwoBox: false,
            value: '',
            regex: '',
        },
    },
    radio: {
        type: FieldTypes.RADIO,
        name: 'Radio Group',
        name_ar: 'مجموعة راديو',
        icon: IconToggleLeft,
        hasOptions: true,
        hasPlaceholder: false,
        hasRegex: false,
        is_visible_kyc: true,
        is_visible_contract: true,
        defaultTemplate: {
            type: FieldTypes.RADIO,
            label: 'Select an option',
            label_ar: 'اختر خيار',
            key: 'radio_field',
            required: true,
            value: '',
            options: [
                { label: 'Option 1', label_ar: 'الخيار 1', value: 'option1' },
                { label: 'Option 2', label_ar: 'الخيار 2', value: 'option2' },
            ],
        },
    },
    checkbox: {
        type: FieldTypes.CHECKBOX,
        name: 'Checkbox Group',
        name_ar: 'مجموعة خانات اختيار',
        icon: IconGripVertical,
        hasOptions: true,
        hasPlaceholder: false,
        hasRegex: false,
        is_visible_kyc: true,
        is_visible_contract: true,
        defaultTemplate: {
            type: FieldTypes.CHECKBOX,
            label: 'Select options',
            label_ar: 'اختر الخيارات',
            key: 'checkbox_field',
            required: true,
            value: [],
            options: [
                { label: 'Option 1', label_ar: 'الخيار 1', value: 'option1' },
                { label: 'Option 2', label_ar: 'الخيار 2', value: 'option2' },
            ],
        },
    },
    select: {
        type: FieldTypes.SELECT,
        name: 'Select Dropdown',
        name_ar: 'قائمة منسدلة',
        icon: IconList,
        hasOptions: true,
        hasPlaceholder: false,
        hasRegex: false,
        is_visible_kyc: true,
        is_visible_contract: true,
        defaultTemplate: {
            type: FieldTypes.SELECT,
            label: 'Choose from dropdown',
            label_ar: 'اختر من القائمة',
            key: 'select_field',
            required: true,
            value: '',
            options: [
                { label: 'Option 1', label_ar: 'الخيار 1', value: 'option1' },
                { label: 'Option 2', label_ar: 'الخيار 2', value: 'option2' },
            ],
        },
    },
    paci: {
        type: FieldTypes.PACI,
        name: 'PACI Authentication',
        name_ar: 'مصادقة PACI',
        icon: IconEdit,
        hasOptions: false,
        hasPlaceholder: true,
        hasRegex: false,
        is_visible_kyc: true,
        is_visible_contract: true,
        defaultTemplate: {
            type: FieldTypes.PACI,
            label: 'PACI Authentication',
            label_ar: 'مصادقة PACI',
            key: 'paci_authentication',
            placeholder:
                'PACI authentication sent to the Kuwait mobile ID application and you need to approve it there',
            placeholder_ar: '',
            required: true,
            value: '',
        },
    },
    docusign: {
        type: FieldTypes.DOCUSIGN,
        name: 'DocuSign Signing',
        name_ar: 'التوقيع عبر DocuSign',
        icon: IconEdit,
        hasOptions: false,
        hasPlaceholder: true,
        hasRegex: false,
        is_visible_kyc: true,
        is_visible_contract: true,
        defaultTemplate: {
            type: FieldTypes.DOCUSIGN,
            label: 'DocuSign Signing',
            label_ar: 'التوقيع عبر DocuSign',
            key: 'docusign_signing',
            placeholder:
                'The generated contract is sent by email for DocuSign remote signature',
            placeholder_ar: '',
            required: true,
            value: '',
        },
    },
    signature: {
        type: FieldTypes.SIGNATURE,
        name: 'Signature Pad',
        name_ar: 'لوحة التوقيع',
        icon: IconEdit,
        hasOptions: false,
        hasPlaceholder: false,
        hasRegex: false,
        is_visible_kyc: true,
        is_visible_contract: true,
        defaultTemplate: {
            type: FieldTypes.SIGNATURE,
            label: 'Signature',
            label_ar: 'التوقيع',
            key: 'signature_field',
            required: true,
            value: '',
        },
    },
    fileUpload: {
        type: FieldTypes.FILE_UPLOAD,
        name: 'File Upload',
        name_ar: 'تحميل الملف',
        icon: IconEdit,
        hasOptions: false,
        hasPlaceholder: false,
        hasRegex: false,
        is_visible_kyc: true,
        is_visible_contract: true,
        defaultTemplate: {
            type: FieldTypes.FILE_UPLOAD,
            label: 'File Upload',
            label_ar: 'تحميل الملف',
            key: 'file_upload_field',
            required: true,
            maxFile: 2,
            fileSize: 10,
            fileExtension: '.jpg,.jpeg,.png,.pdf',
            fileStoragePath: './uploads/',
            filePrompt: 'Check if the uploaded file is clear and readable',
            fileAIChecker: ['chat-gpt', 'gemini'],
            value: '',
        },
    },
    datePicker: {
        type: FieldTypes.DATE_PICKER,
        name: 'Date Picker',
        name_ar: 'اختيار التاريخ',
        icon: IconEdit,
        hasOptions: false,
        hasPlaceholder: false,
        hasRegex: false,
        is_visible_kyc: true,
        is_visible_contract: true,
        defaultTemplate: {
            type: FieldTypes.DATE_PICKER,
            label: 'Date',
            label_ar: 'التاريخ',
            key: 'date_field',
            required: true,
            value: '',
        },
    },
    countryPicker: {
        type: FieldTypes.COUNTRY_PICKER,
        name: 'Country Picker',
        name_ar: 'منتقي الدولة',
        icon: IconEdit,
        hasOptions: false,
        hasPlaceholder: false,
        hasRegex: false,
        is_visible_kyc: true,
        is_visible_contract: true,
        defaultTemplate: {
            type: FieldTypes.COUNTRY_PICKER,
            label: 'Select Country',
            label_ar: 'اختر الدولة',
            key: 'country_field',
            required: true,
            value: '',
        },
    },
    agreementCheckbox: {
        type: FieldTypes.AGREEMENT_CHECKBOX,
        name: 'Agreement',
        name_ar: 'اتفاقية',
        icon: IconCheck,
        hasOptions: false,
        hasPlaceholder: false,
        hasRegex: false,
        is_visible_kyc: true,
        is_visible_contract: true,
        defaultTemplate: {
            type: FieldTypes.AGREEMENT_CHECKBOX,
            label: 'I agree to the terms and conditions',
            label_ar: 'أوافق على الشروط والأحكام',
            key: 'contract_agreement',
            required: true,
            value: false,
            agreementType: AgreementType.Content,
        },
    },
    thankYouTickMark: {
        type: FieldTypes.THANK_YOU_TICK_MARK,
        name: 'Thank You Tick Mark',
        name_ar: 'علامة شكر',
        icon: IconCheck,
        hasOptions: false,
        hasPlaceholder: false,
        hasRegex: false,
        is_visible_kyc: true,
        is_visible_contract: true,
        defaultTemplate: {
            type: FieldTypes.THANK_YOU_TICK_MARK,
            label: 'Thank You!',
            label_ar: 'شكراً لك!',
            key: 'thank_you_tick_mark',
            required: false,
            value: '',
        },
    },
    transferAmount: {
        type: FieldTypes.TRANSFER_AMOUNT,
        name: 'Transfer Amount',
        name_ar: 'مبلغ نقل المحفظة',
        icon: IconEdit,
        hasOptions: true,
        hasPlaceholder: true,
        hasRegex: true,
        is_visible_kyc: true,
        is_visible_contract: true,
        defaultTemplate: {
            type: FieldTypes.TRANSFER_AMOUNT,
            label: 'Transfer Amount',
            label_ar: 'مبلغ النقل',
            key: 'transfer_amount',
            required: true,
            showPlaceholder: false,
            showTwoBox: false,
            value: '',
        },
    },
    downloadContract: {
        type: FieldTypes.DOWNLOAD_CONTRACT,
        name: 'Download Contract',
        name_ar: 'تحميل العقد',
        icon: IconCheck,
        hasOptions: false,
        hasPlaceholder: false,
        hasRegex: false,
        is_visible_kyc: true,
        is_visible_contract: true,
        defaultTemplate: {
            type: FieldTypes.DOWNLOAD_CONTRACT,
            label: 'Download Contract',
            label_ar: 'تحميل العقد',
            key: 'download_contract',
            required: false,
            value: '',
        },
    },
    objectList: {
        type: FieldTypes.OBJECT_LIST,
        name: 'Object List',
        name_ar: 'قائمة عناصر',
        icon: IconList,
        hasOptions: false,
        hasPlaceholder: false,
        hasRegex: false,
        is_visible_kyc: true,
        is_visible_contract: true,
        defaultTemplate: {
            type: FieldTypes.OBJECT_LIST,
            label: 'Items',
            label_ar: 'العناصر',
            key: 'items',
            columns: [
                { key: 'name', label: 'Name', label_ar: 'الاسم' },
            ],
            required: false,
            value: [],
        },
    },
    mobileWithCode: {
        type: FieldTypes.MOBILE_WITH_CODE,
        name: 'Mobile with Code',
        name_ar: 'الهاتف المحمول مع الرمز',
        icon: IconPhone,
        hasOptions: false,
        hasPlaceholder: true,
        hasRegex: true,
        is_visible_kyc: true,
        is_visible_contract: true,
        defaultTemplate: {
            type: FieldTypes.MOBILE_WITH_CODE,
            label: 'Mobile with Code',
            label_ar: 'الهاتف المحمول مع الرمز',
            sub_key: 'country_code',
            key: 'mobile_number',
            required: false,
            value: '',
        },
    },
};

// ==================== UTILITY FUNCTIONS ====================

/**
 * Generates a unique ID for React keys
 * Uses timestamp and random number for uniqueness
 */
const generateUniqueId = (): number => Date.now() + Math.random();

/**
 * Generates a URL-safe key from a label string
 * Converts to lowercase, replaces non-alphanumeric with underscores
 */
const generateFieldKey = (label: string): string => {
    return label
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '');
};

/**
 * Recursively adds unique IDs to objects and arrays for React rendering
 * Ensures all components, fields, options have unique_id property
 */
const addUniqueIds = (obj: any): any => {
    if (Array.isArray(obj)) {
        return obj.map((item) => addUniqueIds(item));
    }

    if (obj && typeof obj === 'object') {
        const newObj = { ...obj };

        if (!newObj.unique_id) {
            newObj.unique_id = generateUniqueId();
        }

        // Process nested objects that need unique IDs
        const nestedKeys = ['options', 'section_fields', 'components', 'action'];
        nestedKeys.forEach((key) => {
            if (newObj[key]) {
                newObj[key] = addUniqueIds(newObj[key]);
            }
        });

        return newObj;
    }

    return obj;
};

/**
 * Recursively removes unique_id fields used only for React rendering
 * Prepares data for Firestore storage
 */
const cleanData = (obj: any): any => {

    // Handle Timestamp conversion first
    if (obj instanceof Timestamp) {
        return obj; // Already a proper Timestamp, return as-is
    }

    // Handle Date conversion to Timestamp
    if (obj instanceof Date) {
        return Timestamp.fromDate(obj);
    }

    // Handle map-like Timestamp objects
    if (
        obj &&
        typeof obj === 'object' &&
        'seconds' in obj &&
        'nanoseconds' in obj
    ) {
        return Timestamp.fromMillis(
            obj.seconds * 1000 + obj.nanoseconds / 1000000
        );
    }

    if (Array.isArray(obj)) {
        return obj.map((item) => cleanData(item));
    }

    if (obj && typeof obj === 'object') {
        const newObj: any = {};

        Object.keys(obj).forEach((key) => {
            if (key !== 'unique_id') {
                newObj[key] = cleanData(obj[key]);
            }
        });

        // Ensure checkbox fields have array value
        if (newObj.type === 'checkbox' && !newObj.hasOwnProperty('value')) {
            newObj.value = [];
        }

        return newObj;
    }

    return obj;
};

/**
 * Gets background color based on nesting level
 */
const getBackgroundColor = (level: number, isOption: boolean = false): string => {
    if (isOption) {
        return NESTING_COLORS[`option${Math.min(level, 3)}` as keyof typeof NESTING_COLORS];
    }
    return NESTING_COLORS[`level${Math.min(level, 3)}` as keyof typeof NESTING_COLORS];
};

/**
 * Gets border color based on nesting level
 */
const getBorderColor = (level: number): string => {
    return BORDER_COLORS[`level${Math.min(level, 4)}` as keyof typeof BORDER_COLORS];
};

// ==================== REUSABLE COMPONENTS ====================

interface FieldInputProps {
    label: string;
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    required?: boolean;
    disabled?: boolean;
    size?: 'xs' | 'sm' | 'md' | 'lg';
    span?: number | { base?: number; sm?: number; md?: number; lg?: number };
}

/**
 * Reusable text input component with consistent styling
 */
const FieldInput: React.FC<FieldInputProps> = React.memo(({
    label,
    value,
    onChange,
    placeholder,
    required = false,
    size = 'md',
    disabled = false,
    span = 6,
}) => (
    <Grid.Col span={span}>
        <TextInput
            label={label}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            required={required}
            disabled={disabled}
            size={size}
        />
    </Grid.Col>
));

FieldInput.displayName = 'FieldInput';

interface BilingualInputProps {
    labelEn: string;
    labelAr: string;
    valueEn: string;
    valueAr: string;
    onChangeEn: (value: string) => void;
    onChangeAr: (value: string) => void;
    placeholderEn?: string;
    placeholderAr?: string;
    required?: boolean;
    size?: 'xs' | 'sm' | 'md' | 'lg';
    span?: number | { base?: number; sm?: number; md?: number; lg?: number };
}

/**
 * Bilingual input component for English and Arabic text fields
 */
const BilingualInput: React.FC<BilingualInputProps> = React.memo(({
    labelEn,
    labelAr,
    valueEn,
    valueAr,
    onChangeEn,
    onChangeAr,
    placeholderEn,
    placeholderAr,
    required = true,
    size = 'md',
    span = { base: 12, sm: 6 },
}) => (
    <>
        <FieldInput
            label={labelEn}
            value={valueEn}
            onChange={onChangeEn}
            placeholder={placeholderEn}
            required={required}
            size={size}
            span={span}
        />
        <FieldInput
            label={labelAr}
            value={valueAr}
            onChange={onChangeAr}
            placeholder={placeholderAr}
            required={required}
            size={size}
            span={span}
        />
    </>
));

BilingualInput.displayName = 'BilingualInput';

interface BilingualTextareaProps {
    labelEn: string;
    labelAr: string;
    valueEn: string;
    valueAr: string;
    onChangeEn: (value: string) => void;
    onChangeAr: (value: string) => void;
    minRows?: number;
    span?: number | { base?: number; sm?: number; md?: number; lg?: number };
}

/**
 * Bilingual textarea component for multi-line English and Arabic text
 */
const BilingualTextarea: React.FC<BilingualTextareaProps> = React.memo(({
    labelEn,
    labelAr,
    valueEn,
    valueAr,
    onChangeEn,
    onChangeAr,
    minRows = 3,
    span = { base: 12, md: 6 },
}) => (
    <>
        <Grid.Col span={span}>
            <Textarea
                label={labelEn}
                value={valueEn}
                onChange={(e) => onChangeEn(e.target.value)}
                minRows={minRows}
            />
        </Grid.Col>
        <Grid.Col span={span}>
            <Textarea
                label={labelAr}
                value={valueAr}
                onChange={(e) => onChangeAr(e.target.value)}
                minRows={minRows}
            />
        </Grid.Col>
    </>
));

BilingualTextarea.displayName = 'BilingualTextarea';

interface FieldTypeSelectorProps {
    fieldTypes: FieldTypeConfig[];
    onSelect: (type: FieldTypes) => void;
    showQuickAdd?: boolean;
    onQuickAdd?: () => void;
    size?: 'xs' | 'sm' | 'md' | 'lg';
}

/**
 * Field type selector with optional quick-add button for common field types
 */
const FieldTypeSelector: React.FC<FieldTypeSelectorProps> = React.memo(({
    fieldTypes,
    onSelect,
    showQuickAdd = true,
    onQuickAdd,
    size = 'sm',
}) => (
    <Group gap="xs">
        <Select
            placeholder="Select field type"
            size={size}
            data={fieldTypes.map((type) => ({ value: type.type, label: type.name }))}
            onChange={(value) => value && onSelect(value as FieldTypes)}
            style={{ minWidth: size === 'xs' ? 120 : 150 }}
        />
        {showQuickAdd && onQuickAdd && (
            <Button
                size={size}
                variant="light"
                leftSection={<IconPlus size={16} />}
                onClick={onQuickAdd}
            >
                Quick Add Input
            </Button>
        )}
    </Group>
));

FieldTypeSelector.displayName = 'FieldTypeSelector';

interface ActionEditorProps {
    action: PageAction;
    onUpdate: (field: keyof PageAction, value: any) => void;
    onRemove: () => void;
}

/**
 * Editor component for page action configuration (buttons, links, submit actions)
 */
const ActionEditor: React.FC<ActionEditorProps> = React.memo(({ action, onUpdate, onRemove }) => (
    <Card withBorder p="md">
        <Grid>
            <Grid.Col span={{ base: 12, sm: 6, md: 3, lg: 6 }}>
                <Select
                    label="Action Type"
                    value={action.type}
                    required
                    onChange={(value) => value && onUpdate('type', value)}
                    data={[
                        { value: 'button', label: 'Button' },
                        { value: 'link', label: 'Link' },
                        { value: 'submit', label: 'Submit' },
                    ]}
                />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6, md: 3, lg: 6 }}>
                <TextInput
                    label="Action Key"
                    value={action.key}
                    onChange={(e) => onUpdate('key', e.target.value)}
                    required
                />
            </Grid.Col>
            <BilingualInput
                labelEn="Label (English)"
                labelAr="Label (Arabic)"
                valueEn={action.label}
                valueAr={action.label_ar}
                onChangeEn={(value) => onUpdate('label', value)}
                onChangeAr={(value) => onUpdate('label_ar', value)}
                span={{ base: 12, sm: 12, md: 12, lg: 6 }}
            />
            <Grid.Col span={{ base: 12, sm: 12, md: 12, lg: 3 }}>
                <Group justify="center" gap="md">
                    <Switch
                        mt="xs"
                        label="Save Data"
                        checked={action.is_save}
                        onChange={(e) => onUpdate('is_save', e.currentTarget.checked)}
                    />
                    <Switch
                        mt="xs"
                        label="Visible"
                        checked={action.visible}
                        onChange={(e) => onUpdate('visible', e.currentTarget.checked)}
                    />
                    <Button color="red" leftSection={<IconTrash />} onClick={onRemove}>
                        Remove
                    </Button>
                </Group>
            </Grid.Col>
        </Grid>
    </Card>
));

ActionEditor.displayName = 'ActionEditor';

interface OptionEditorProps {
    option: ComponentOption;
    optionIndex: number;
    level?: number;
    onUpdate: (field: keyof ComponentOption, value: string) => void;
    onRemove: () => void;
    onToggleExpand?: () => void;
    isExpanded?: boolean;
    required?: boolean;
    showNestedFields?: boolean;
    children?: React.ReactNode;
    fieldType: FieldTypes;
}

/**
 * Editor component for radio/checkbox/select options with nested field support
 */
const OptionEditor: React.FC<OptionEditorProps> = React.memo(({
    option,
    onUpdate,
    onRemove,
    onToggleExpand,
    isExpanded = false,
    required = true,
    showNestedFields = true,
    level = 0,
    children,
    fieldType,
}) => {
    const bgColor = getBackgroundColor(level, true);
    const size = level > 0 ? 'xs' : 'sm';

    return (
        <Card withBorder p="sm" mb="xs" style={{ backgroundColor: bgColor }}>
            <Grid>
                <Grid.Col span={{ base: 12, sm: 12, md: 4, lg: 3 }}>
                    <TextInput
                        label="Option Label (English)"
                        value={option.label}
                        size={size}
                        required={required}
                        onChange={(e) => onUpdate('label', e.target.value)}
                    />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 12, md: 4, lg: 3 }}>
                    <TextInput
                        label="Option Label (Arabic)"
                        value={option.label_ar}
                        size={size}
                        required={required}
                        onChange={(e) => onUpdate('label_ar', e.target.value)}
                    />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 8, md: 3, lg: 2 }}>
                    <TextInput
                        label="Option Value"
                        value={option.value}
                        size={size}
                        required={required}
                        onChange={(e) => onUpdate('value', e.target.value)}
                    />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 4, md: 1, lg: 4 }}>
                    <Text size="sm" mb="xs">
                        Actions
                    </Text>
                    <Group gap="xs">
                        {showNestedFields && onToggleExpand && (
                            <ActionIcon
                                variant="light"
                                color="blue"
                                size={level > 0 ? 'xs' : 'sm'}
                                onClick={onToggleExpand}
                                aria-label={isExpanded ? 'Collapse' : 'Expand'}
                            >
                                {isExpanded ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
                            </ActionIcon>
                        )}
                        <ActionIcon
                            variant="light"
                            color="red"
                            size={level > 0 ? 'xs' : 'sm'}
                            onClick={onRemove}
                            aria-label="Remove option"
                        >
                            <IconTrash size={14} />
                        </ActionIcon>
                    </Group>
                </Grid.Col>

                <Grid.Col span={{ base: 12, sm: 12, md: 6 }}>
                    <TextFieldEditor
                        label="Content (English)"
                        value={option.placeholder || ''}
                        onChange={(value) => onUpdate('placeholder', value)}
                        onRemove={() => onUpdate('placeholder', '')}
                        language="en"
                        placeholder="Click to add/edit rich text content"
                    />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 12, md: 6 }}>
                    <TextFieldEditor
                        label="المحتوى (بالعربية)"
                        value={option.placeholder_ar || ''}
                        onChange={(value) => onUpdate('placeholder_ar', value)}
                        onRemove={() => onUpdate('placeholder_ar', '')}
                        language="ar"
                        placeholder="انقر لإضافة/تعديل محتوى نص منسق"
                    />
                </Grid.Col>

                {(fieldType === FieldTypes.CHECKBOX) && (
                    <>
                        <Grid.Col span={{ base: 12, sm: 12, md: 6 }}>
                            <TextFieldEditor
                                label="Tooltip (English)"
                                value={option.tooltip || ''}
                                onChange={(value) => onUpdate('tooltip', value)}
                                onRemove={() => onUpdate('tooltip', '')}
                                language="en"
                                placeholder="Click to add/edit rich text content"
                            />
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, sm: 12, md: 6 }}>
                            <TextFieldEditor
                                label="التلميح (بالعربية)"
                                value={option.tooltip_ar || ''}
                                onChange={(value) => onUpdate('tooltip_ar', value)}
                                onRemove={() => onUpdate('tooltip_ar', '')}
                                language="ar"
                                placeholder="انقر لإضافة/تعديل محتوى نص منسق"
                            />
                        </Grid.Col>
                    </>)}

                <Grid.Col span={12}>
                    <ConditionField
                        label="Disabled When (Optional)"
                        value={option.disabled_when || ''}
                        onChange={(value) => onUpdate('disabled_when', value)}
                        onRemove={() => onUpdate('disabled_when', '')}
                        conditionFalseLabel="This option is visible mode"
                        conditionTrueLabel="This option is disabled mode"
                    />
                </Grid.Col>
                <Grid.Col span={12}>
                    <ConditionField
                        label="On Select JS (Optional)"
                        value={option.on_select || ''}
                        onChange={(value) => onUpdate('on_select', value)}
                        onRemove={() => onUpdate('on_select', '')}
                        placeholder="formData.skip_page_tags = ['kyc-form'];"
                        conditionFalseLabel="No form data changes"
                        conditionTrueLabel="Form data changed"
                        editorMode="mutation"
                        modalSubtitle="Configure your option selection script"
                        saveLabel="Save Script"
                    />
                </Grid.Col>
                {fieldType === FieldTypes.CHECKBOX && (
                    <Grid.Col span={12}>
                        <ConditionField
                            label="On Unselect JS (Optional)"
                            value={option.on_unselect || ''}
                            onChange={(value) => onUpdate('on_unselect', value)}
                            onRemove={() => onUpdate('on_unselect', '')}
                            placeholder="delete formData.skip_page_tags;"
                            conditionFalseLabel="No form data changes"
                            conditionTrueLabel="Form data changed"
                            editorMode="mutation"
                            modalSubtitle="Configure your option unselect script"
                            saveLabel="Save Script"
                        />
                    </Grid.Col>
                )}
            </Grid>
            {isExpanded && showNestedFields && children}
        </Card>
    );
});

OptionEditor.displayName = 'OptionEditor';

// ==================== UNIFIED FIELD EDITOR COMPONENT ====================

const ObjectListColumnsEditor: React.FC<{
    columns: NonNullable<SectionField['columns']>;
    onChange: (columns: NonNullable<SectionField['columns']>) => void;
}> = ({ columns, onChange }) => {
    const updateColumn = (
        index: number,
        key: keyof NonNullable<SectionField['columns']>[number],
        value: string,
    ) => {
        onChange(columns.map((column, columnIndex) =>
            columnIndex === index ? { ...column, [key]: value } : column,
        ));
    };

    return (
        <Grid.Col span={12}>
            <Card withBorder p="sm">
                <Group justify="space-between" mb="sm">
                    <div>
                        <Text fw={500}>Object List Columns</Text>
                        <Text size="xs" c="dimmed">Each column reads a property from every object in the field value array.</Text>
                    </div>
                    <Button
                        size="xs"
                        leftSection={<IconPlus size={14} />}
                        onClick={() => onChange([
                            ...columns,
                            { key: `column_${columns.length + 1}`, label: 'Column', label_ar: 'عمود' },
                        ])}
                    >
                        Add Column
                    </Button>
                </Group>
                <Stack gap="xs">
                    {columns.map((column, index) => (
                        <Grid key={`${column.key}-${index}`} align="end">
                            <Grid.Col span={{ base: 12, md: 3 }}>
                                <TextInput label="Object Key" required value={column.key} onChange={(event) => updateColumn(index, 'key', event.target.value)} />
                            </Grid.Col>
                            <Grid.Col span={{ base: 12, md: 2 }}>
                                <TextInput label="Arabic Value Key" description="Optional" value={column.key_ar || ''} onChange={(event) => updateColumn(index, 'key_ar', event.target.value)} />
                            </Grid.Col>
                            <Grid.Col span={{ base: 12, md: 3 }}>
                                <TextInput label="Heading (English)" required value={column.label} onChange={(event) => updateColumn(index, 'label', event.target.value)} />
                            </Grid.Col>
                            <Grid.Col span={{ base: 12, md: 3 }}>
                                <TextInput label="Heading (Arabic)" required value={column.label_ar} onChange={(event) => updateColumn(index, 'label_ar', event.target.value)} />
                            </Grid.Col>
                            <Grid.Col span={{ base: 12, md: 1 }}>
                                <ActionIcon color="red" variant="light" aria-label="Remove column" onClick={() => onChange(columns.filter((_, columnIndex) => columnIndex !== index))}>
                                    <IconTrash size={14} />
                                </ActionIcon>
                            </Grid.Col>
                        </Grid>
                    ))}
                    {columns.length === 0 && <Text size="sm" c="red">Add at least one column.</Text>}
                </Stack>
            </Card>
        </Grid.Col>
    );
};

const FieldDatePicker: React.FC<{
    onUpdate: (key: 'firstDate' | 'lastDate', date: Timestamp | null) => void;
    initialFirstDate?: Timestamp | null;
    initialLastDate?: Timestamp | null;
}> = React.memo(({
    onUpdate,
    initialFirstDate,
    initialLastDate
}) => {
    // Convert Timestamps to Date if they exist
    const [firstDate, setFirstDate] = useState<Date | null>(
        initialFirstDate ? initialFirstDate.toDate() : null
    );
    const [lastDate, setLastDate] = useState<Date | null>(
        initialLastDate ? initialLastDate.toDate() : null
    );

    // useEffect to handle changes in initial dates
    useEffect(() => {
        setFirstDate(initialFirstDate ? initialFirstDate.toDate() : null);
        setLastDate(initialLastDate ? initialLastDate.toDate() : null);
    }, [initialFirstDate, initialLastDate]);

    const handleUpdate = (key: 'firstDate' | 'lastDate', date: Date | null) => {
        // Explicitly create a Timestamp or set to null
        const ts = date ? Timestamp.fromDate(date) : null;

        if (key === 'firstDate') {
            // if firstDate > lastDate, clear lastDate
            if (date && lastDate && date > lastDate) {
                setLastDate(null);
                onUpdate('lastDate', null);
            }
            setFirstDate(date);
            onUpdate('firstDate', ts);
        } else {
            // if lastDate < firstDate, clear firstDate
            if (date && firstDate && date < firstDate) {
                setFirstDate(null);
                onUpdate('firstDate', null);
            }
            setLastDate(date);
            onUpdate('lastDate', ts);
        }
    };

    return (
        <>
            <Grid.Col span={{ base: 12, sm: 12, md: 6 }}>
                <DatePickerInput
                    label="First Date"
                    value={firstDate}
                    onChange={(date) => handleUpdate('firstDate', fromMantineDate(date))}
                    clearable
                    placeholder="Select first date"
                    maxDate={lastDate ?? undefined}
                />
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 12, md: 6 }}>
                <DatePickerInput
                    label="Last Date"
                    value={lastDate}
                    onChange={(date) => handleUpdate('lastDate', fromMantineDate(date))}
                    clearable
                    placeholder="Select last date"
                    minDate={firstDate ?? undefined}
                />
            </Grid.Col>
        </>
    );
});

interface FieldEditorProps {
    field: SectionField;
    fieldIndex: number;
    fieldTypes: FieldTypeConfig[];
    level?: number;
    onUpdate: (property: keyof SectionField, value: any) => void;
    onRemove: () => void;
    onTypeChange?: (newType: FieldTypes) => void;
    children?: React.ReactNode;

    moveable?: boolean;
    onMoveUp?: () => void;
    onMoveDown?: () => void;
    isFirst?: boolean;
    isLast?: boolean;
}

/**
 * Comprehensive field editor supporting all field types with conditional rendering
 * Handles validation, options, nested fields, and type-specific configurations
 */
const FieldEditor: React.FC<FieldEditorProps> = React.memo(({
    field,
    fieldIndex,
    fieldTypes,
    level = 0,
    onUpdate,
    onRemove,
    onTypeChange,
    children,
    onMoveUp,
    onMoveDown,
    isFirst,
    isLast,
    moveable,
}) => {

    const fieldConfig = useMemo(
        () => fieldTypes.find((ft) => ft.type === field.type),
        [fieldTypes, field.type]
    );

    const backgroundColor = getBackgroundColor(level);
    const borderColor = level > 0 ? getBorderColor(level) : undefined;

    // Determine which controls to hide based on field type
    const isHideContractControls = !CONTRACT_CONTROL_HIDDEN_TYPES.has(field.type);


    return (
        <Card
            withBorder
            p="md"
            mb="md"
            style={{
                backgroundColor,
                marginLeft: level > 0 ? level * 15 : 0,
                borderLeft: level > 0 ? `3px solid ${borderColor}` : undefined,
            }}
        >
            {/* Field header with controls */}
            <Group justify="space-between" mb="md">
                <Group gap="sm">

                    {/* Add Up/Down buttons for level 0 fields */}
                    {moveable && level === 0 && onMoveUp && onMoveDown && (
                        <Group gap="xs">
                            <ActionIcon
                                size="sm"
                                variant="light"
                                color="blue"
                                onClick={onMoveUp}
                                disabled={isFirst}
                                aria-label="Move up"
                                style={{
                                    opacity: isFirst ? 0.4 : 1,
                                    cursor: isFirst ? 'not-allowed' : 'pointer',
                                }}
                            >
                                <IconArrowUp size={14} />
                            </ActionIcon>
                            <ActionIcon
                                size="sm"
                                variant="light"
                                color="blue"
                                onClick={onMoveDown}
                                disabled={isLast}
                                aria-label="Move down"
                                style={{
                                    opacity: isLast ? 0.4 : 1,
                                    cursor: isLast ? 'not-allowed' : 'pointer',
                                }}
                            >
                                <IconArrowDown size={14} />
                            </ActionIcon>
                        </Group>
                    )}


                    {level > 0 ? (
                        <Badge
                            variant="light"
                            color={
                                level === 1 ? 'blue' : level === 2 ? 'green' : level === 3 ? 'yellow' : 'red'
                            }
                            size="sm"
                        >
                            Level {level} - {fieldConfig?.name || field.type}
                        </Badge>
                    ) : (
                        <>
                            <Text fw={500}>Field {fieldIndex + 1}</Text>
                            <Badge size="sm" variant="light">
                                {fieldConfig?.name || field.type}
                            </Badge>
                        </>
                    )}
                </Group>

                <Switch
                    label="Visible on App"
                    checked={field.is_visible_kyc}
                    onChange={(e) => onUpdate('is_visible_kyc', e.currentTarget.checked)}
                />
                <Switch
                    label="Visible on PDF"
                    checked={field.is_visible_contract}
                    onChange={(e) => onUpdate('is_visible_contract', e.currentTarget.checked)}
                />
                {isHideContractControls && (
                    <>
                        <Switch
                            label="Show Placeholder"
                            checked={field.showPlaceholder || false}
                            onChange={(e) => onUpdate('showPlaceholder', e.currentTarget.checked)}
                        />
                        <Switch
                            label="Two Box Layout"
                            checked={field.showTwoBox || false}
                            onChange={(e) => onUpdate('showTwoBox', e.currentTarget.checked)}
                        />
                    </>
                )}
                <ActionIcon
                    size="sm"
                    variant="light"
                    color="red"
                    onClick={onRemove}
                    aria-label="Remove field"
                >
                    <IconTrash size={12} />
                </ActionIcon>
            </Group>

            {/* Field configuration grid */}
            <Grid>
                {(() => {
                    const isMobile = field.type === FieldTypes.MOBILE_WITH_CODE;
                    const colSpan = { base: 12, sm: 6, md: isMobile ? 3 : 4 };

                    return (
                        <>
                            <Grid.Col span={colSpan}>
                                <Select
                                    label="Field Type"
                                    value={field.type}
                                    required
                                    onChange={(value) =>
                                        value && (onTypeChange ? onTypeChange(value as FieldTypes) : onUpdate('type', value))
                                    }
                                    data={fieldTypes.map((type) => ({ value: type.type, label: type.name }))}
                                />
                            </Grid.Col>

                            <Grid.Col span={colSpan}>
                                <TextInput
                                    label={isMobile ? 'Mobile Number Key' : 'Field Key'}
                                    value={field.key || ''}
                                    required
                                    onChange={(e) => onUpdate('key', e.target.value)}
                                />
                            </Grid.Col>

                            {isMobile && (
                                <Grid.Col span={colSpan}>
                                    <TextInput
                                        label="Country Code Key"
                                        placeholder="country_code"
                                        value={field.sub_key || ''}
                                        required
                                        onChange={(e) => onUpdate('sub_key', e.target.value)}
                                    />
                                </Grid.Col>
                            )}

                            <Grid.Col span={isMobile ? colSpan : { base: 12, sm: 12, md: 4 }}>
                                <Select
                                    label="Field Required"
                                    value={field.required ? 'yes' : 'no'}
                                    disabled={['text', 'thankYouTickMark', 'downloadContract'].includes(field.type)}
                                    onChange={(value) => onUpdate('required', value === 'yes')}
                                    data={[
                                        { value: 'yes', label: 'Yes' },
                                        { value: 'no', label: 'No' },
                                    ]}
                                />
                            </Grid.Col>
                        </>
                    );
                })()}

                {/* Text field with rich text editor */}
                {(field.type === 'text' || field.type === 'thankYouTickMark') ? (
                    <>
                        <BilingualInput
                            labelEn="Label (English)"
                            labelAr="Arabic Label"
                            valueEn={field.label || ''}
                            valueAr={field.label_ar || ''}
                            required={false}
                            onChangeEn={(value) => onUpdate('label', value)}
                            onChangeAr={(value) => onUpdate('label_ar', value)}
                        />
                        <Grid.Col span={{ base: 12, sm: 12, md: 6 }}>
                            <TextFieldEditor
                                label="Content (English)"
                                value={field.placeholder || ''}
                                onChange={(value) => onUpdate('placeholder', value)}
                                onRemove={() => onUpdate('placeholder', '')}
                                language="en"
                                placeholder="Click to add/edit rich text content"
                            />
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, sm: 12, md: 6 }}>
                            <TextFieldEditor
                                label="المحتوى (بالعربية)"
                                value={field.placeholder_ar || ''}
                                onChange={(value) => onUpdate('placeholder_ar', value)}
                                onRemove={() => onUpdate('placeholder_ar', '')}
                                language="ar"
                                placeholder="انقر لإضافة/تعديل محتوى نص منسق"
                            />
                        </Grid.Col>
                    </>
                ) : (
                    <BilingualInput
                        labelEn="Label (English)"
                        labelAr="Arabic Label"
                        valueEn={field.label || ''}
                        valueAr={field.label_ar || ''}
                        onChangeEn={(value) => onUpdate('label', value)}
                        onChangeAr={(value) => onUpdate('label_ar', value)}
                    />
                )}

                {field.type === 'input' && (
                    <BilingualInput
                        labelEn="Value (English)"
                        labelAr="Value (Arabic)"
                        valueEn={field.value || ''}
                        valueAr={field.value_ar || ''}
                        placeholderEn="Use Variable {{user.name}}"
                        placeholderAr="استخدم المتغير {{user.name}}"
                        required={false}
                        onChangeEn={(value) => onUpdate('value', value)}
                        onChangeAr={(value) => onUpdate('value_ar', value)}
                    />
                )}
                {(field.type === 'input' || field.type === 'datePicker' || field.type === 'mobileWithCode') && (
                    <Grid.Col span={{ base: 12, lg: 12 }}>
                        <ConditionField
                            label="Field Condition (Optional)"
                            value={field.condition || ''}
                            onChange={(v) => onUpdate('condition', v)}
                            onRemove={() => onUpdate('condition', '')}
                            conditionFalseLabel="This field can be shown to the user"
                            conditionTrueLabel="This Field is hidden"
                        />
                    </Grid.Col>
                )}

                {field.type === FieldTypes.OBJECT_LIST && (
                    <ObjectListColumnsEditor
                        columns={field.columns ?? []}
                        onChange={(columns) => onUpdate('columns', columns)}
                    />
                )}

                {/* Placeholder fields for input types */}
                {fieldConfig?.hasPlaceholder && (
                    <BilingualInput
                        labelEn="Placeholder (English)"
                        labelAr="Placeholder (Arabic)"
                        valueEn={field.placeholder || ''}
                        valueAr={field.placeholder_ar || ''}
                        onChangeEn={(value) => onUpdate('placeholder', value)}
                        onChangeAr={(value) => onUpdate('placeholder_ar', value)}
                    />
                )}

                {field.type === 'datePicker' ? (
                    <FieldDatePicker onUpdate={onUpdate}
                        initialFirstDate={field.firstDate}
                        initialLastDate={field.lastDate}
                    />
                ) : null}

                {field.type === 'agreementCheckbox' ? (
                    <>
                        {/* Agreement Type Radio */}
                        <Grid.Col span={{ base: 12, sm: 12, md: 12 }}>
                            <Radio.Group
                                label="Agreement Type"
                                value={field.agreementType ?? AgreementType.Content}
                                styles={{
                                    label: { display: 'block', textAlign: 'left', fontWeight: 500, marginBottom: 8 }
                                }}
                                onChange={(value) => {
                                    const agreementType = value as AgreementType;
                                    onUpdate('agreementType', agreementType);
                                    if (agreementType === AgreementType.ShownInFinalAgreement) {
                                        onUpdate('placeholder', 'shown_in_final_agreement_checkbox');
                                        onUpdate('placeholder_ar', 'shown_in_final_agreement_checkbox');
                                    } else if (agreementType === AgreementType.URL) {
                                        onUpdate('placeholder', '');
                                        onUpdate('placeholder_ar', '');
                                    } else {
                                        onUpdate('placeholder', '');
                                        onUpdate('placeholder_ar', '');
                                    }
                                }}
                            >
                                <Group gap="md" mt={4}>
                                    <Radio value={AgreementType.Content} label="Content" />
                                    <Radio value={AgreementType.URL} label="URL" />
                                    <Radio
                                        value={AgreementType.ShownInFinalAgreement}
                                        label="Shown in Final Agreement"
                                    />
                                </Group>
                            </Radio.Group>
                        </Grid.Col>

                        {/* URL Input */}
                        {field.agreementType === AgreementType.URL && (
                            <Grid.Col span={{ base: 12, sm: 12, md: 12 }}>
                                <TextInput
                                    label="Enter Link URL"
                                    value={field.placeholder || ''}
                                    required
                                    onChange={(e) => {
                                        const url = e.target.value;
                                        onUpdate('placeholder', url);
                                        onUpdate('placeholder_ar', url);
                                    }}
                                />
                            </Grid.Col>
                        )}

                        {/* Rich Text Editors - only for Content type */}
                        {(!field.agreementType || field.agreementType === AgreementType.Content) && (
                            <>
                                <Grid.Col span={{ base: 12, sm: 12, md: 6 }}>
                                    <TextFieldEditor
                                        label="Content (English)"
                                        value={field.placeholder || ''}
                                        onChange={(value) => onUpdate('placeholder', value)}
                                        onRemove={() => onUpdate('placeholder', '')}
                                        language="en"
                                        placeholder="Click to add/edit rich text content"
                                    />
                                </Grid.Col>
                                <Grid.Col span={{ base: 12, sm: 12, md: 6 }}>
                                    <TextFieldEditor
                                        label="المحتوى (بالعربية)"
                                        value={field.placeholder_ar || ''}
                                        onChange={(value) => onUpdate('placeholder_ar', value)}
                                        onRemove={() => onUpdate('placeholder_ar', '')}
                                        language="ar"
                                        placeholder="انقر لإضافة/تعديل محتوى نص منسق"
                                    />
                                </Grid.Col>
                            </>
                        )}

                        {/* Condition Field */}
                        <Grid.Col span={{ base: 12, lg: 12 }}>
                            <ConditionField
                                label="Field Condition (Optional)"
                                value={field.condition || ''}
                                onChange={(v) => onUpdate('condition', v)}
                                onRemove={() => onUpdate('condition', '')}
                                conditionFalseLabel="This field can be shown to the user"
                                conditionTrueLabel="This Field is hidden"
                            />
                        </Grid.Col>
                    </>
                ) : null}

                {/* Regex validation for required fields */}
                {fieldConfig?.hasRegex && field.required && fieldConfig?.type !== 'transferAmount' && (
                    <Grid.Col span={12}>
                        <SimpleRegexSelector
                            value={field.regex || ''}
                            onChange={(value) => onUpdate('regex', value)}
                            required
                        />
                    </Grid.Col>
                )}

                {/* File upload specific configuration */}
                {fieldConfig?.type === 'fileUpload' && (
                    <>
                        <Grid.Col span={{ base: 12, lg: 4 }}>
                            <TextInput
                                label="File Max Count"
                                value={field.maxFile || ''}
                                onChange={(e) => {
                                    const val = parseInt(e.target.value) || 0;
                                    onUpdate('maxFile', Math.max(1, val));
                                }}
                                required
                                type="number"
                                placeholder="1"
                                min={1}
                                max={5}
                            />
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, lg: 4 }}>
                            <TextInput
                                label="File Max Size (in MB)"
                                value={field.fileSize || ''}
                                onChange={(e) => {
                                    const val = parseInt(e.target.value) || 0;
                                    onUpdate('fileSize', Math.max(1, val));
                                }}
                                required
                                type="number"
                                placeholder="2"
                                min={1}
                                max={3}
                            />
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, lg: 4 }}>
                            <TextInput
                                label="File Extensions (comma separated)"
                                value={field.fileExtension || ''}
                                onChange={(e) => onUpdate('fileExtension', e.target.value)}
                                required
                                placeholder=".jpg,.jpeg,.png,.pdf"
                            />
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, lg: 4 }}>
                            <TextInput
                                label="File Storage Path"
                                value={field.fileStoragePath || ''}
                                onChange={(e) => onUpdate('fileStoragePath', e.target.value)}
                                placeholder="user_uploads/{userId}/"
                            />
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, lg: 4 }}>
                            <TextInput
                                label="File AI Checker Prompt"
                                value={field.filePrompt || ''}
                                onChange={(e) => onUpdate('filePrompt', e.target.value)}
                                placeholder="Check if the uploaded image is clear and readable"
                            />
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, lg: 4 }}>
                            <Select
                                label="File AI Checker Models"
                                value={field.fileAIChecker?.[0] || null}
                                onChange={(value) => onUpdate('fileAIChecker', value ? [value] : [])}
                                data={[
                                    { value: 'chat-gpt', label: 'Chat GPT' },
                                    { value: 'gemini', label: 'Gemini' },
                                ]}
                                placeholder="Select AI models"
                            />
                        </Grid.Col>
                    </>
                )}


                {/* Field types that support tooltip (radio, checkbox, text, select, signature)*/}
                {TOOLTIP_SUPPORTING_TYPES.has(field.type) && (
                    <>
                        <Grid.Col span={{ base: 12, sm: 12, md: 6 }}>
                            <TextFieldEditor
                                label="Tooltip (English)"
                                value={field.tooltip || ''}
                                onChange={(value) => onUpdate('tooltip', value)}
                                onRemove={() => onUpdate('tooltip', '')}
                                language="en"
                                placeholder="Click to add/edit rich text content"
                            />
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, sm: 12, md: 6 }}>
                            <TextFieldEditor
                                label="التلميح (بالعربية)"
                                value={field.tooltip_ar || ''}
                                onChange={(value) => onUpdate('tooltip_ar', value)}
                                onRemove={() => onUpdate('tooltip_ar', '')}
                                language="ar"
                                placeholder="انقر لإضافة/تعديل محتوى نص منسق"
                            />
                        </Grid.Col>
                    </>
                )}

                {/* Nested children (options, nested fields, etc.) */}
                {children && <Grid.Col span={12}>{children}</Grid.Col>}
            </Grid>
        </Card>
    );
});

FieldEditor.displayName = 'FieldEditor';

// ==================== CUSTOM HOOKS ====================

/**
 * Hook for managing expanded options state
 */
const useExpandedOptions = () => {
    const [expandedOptions, setExpandedOptions] = useState<Set<string>>(new Set());

    const toggleExpanded = useCallback((optionId: string) => {
        setExpandedOptions((prev) => {
            const newExpanded = new Set(prev);
            if (newExpanded.has(optionId)) {
                newExpanded.delete(optionId);
            } else {
                newExpanded.add(optionId);
            }
            return newExpanded;
        });
    }, []);

    return { expandedOptions, toggleExpanded };
};

/**
 * Hook for managing field types configuration
 */
const useFieldTypes = () => {
    return useMemo(() => Object.values(FIELD_TYPES_CONFIG), []);
};

/**
 * Hook for loading page data from Firestore
 */
const useLoadPageData = (pageID: string | undefined, db: any) => {
    const [isLoading, setIsLoading] = useState(true);
    const [isEditMode, setIsEditMode] = useState(false);
    const [pageData, setPageData] = useState<PageData>({
        id: '',
        title: '',
        title_ar: '',
        description: '',
        description_ar: '',
        eTag: '',
        components: [],
        action: [
            {
                type: 'button',
                label: 'Next',
                label_ar: 'التالي',
                key: 'next',
                is_save: true,
                visible: true,
                unique_id: Date.now(),
            },
        ],
    });

    useEffect(() => {
        const loadData = async () => {
            try {
                if (pageID && pageID !== 'new') {
                    setIsEditMode(true);
                    const docRef = doc(
                        db,
                        FIRESTORE_PATHS.CONFIG,
                        FIRESTORE_PATHS.KYC_CONFIG,
                        FIRESTORE_PATHS.KYC_PAGES,
                        pageID
                    );
                    const docSnap = await getDoc(docRef);

                    if (docSnap.exists()) {
                        const data = docSnap.data() as PageData;
                        const dataWithIds = addUniqueIds(data);
                        setPageData(dataWithIds);
                    } else {
                        showNotification({
                            color: 'red',
                            icon: <IconCheck />,
                            withBorder: true,
                            message: 'Page not found',
                            autoClose: true,
                        });
                    }
                }
            } catch (error) {
                console.error('Error loading data:', error);
                showNotification({
                    color: 'red',
                    icon: <IconCheck />,
                    withBorder: true,
                    message: 'Failed to load page data',
                    autoClose: true,
                });
            } finally {
                setIsLoading(false);
            }
        };

        loadData();
    }, [pageID, db]);

    return { isLoading, isEditMode, pageData, setPageData };
};

// ==================== VALIDATION ====================

/**
 * Validates all required fields in the page configuration
 */
const validatePageData = (
    pageData: PageData,
    getFieldTypeConfig: (type: FieldTypes) => FieldTypeConfig | undefined
): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    // Page-level validation
    if (!pageData.id?.trim()) errors.push('Page ID is required');
    if (!pageData.title?.trim()) errors.push('Page Title (English) is required');
    if (!pageData.title_ar?.trim()) errors.push('Page Title (Arabic) is required');

    if (pageData.components.length === 0) {
        errors.push('At least one section is required');
    }

    // Component validation
    pageData.components.forEach((component, compIndex) => {
        if (!component.section_title?.trim() && component.is_visible_title) {
            errors.push(`Section ${compIndex + 1}: Title (English) is required`);
        }
        if (!component.section_title_ar?.trim() && component.is_visible_title) {
            errors.push(`Section ${compIndex + 1}: Title (Arabic) is required`);
        }
        if (!component.section_id?.trim()) {
            errors.push(`Section ${compIndex + 1}: ID is required`);
        }

        // Field validation
        component.section_fields.forEach((field, fieldIndex) => {
            const fieldLabel = field.label || `Field ${fieldIndex + 1}`;

            if (!field.key?.trim()) {
                errors.push(`Section ${compIndex + 1}, ${fieldLabel}: Field Key is required`);
            }
            if (!field.label?.trim() && field.required) {
                errors.push(`Section ${compIndex + 1}, Field ${fieldIndex + 1}: Label (English) is required`);
            }
            if (!field.label_ar?.trim() && field.required) {
                errors.push(`Section ${compIndex + 1}, ${fieldLabel}: Label (Arabic) is required`);
            }

            // Regex validation
            if (field.required && field.regex !== undefined && field.type !== 'text') {
                const fieldConfig = getFieldTypeConfig(field.type);
                if (fieldConfig?.hasRegex && !field.regex?.trim()) {
                    errors.push(
                        `Section ${compIndex + 1}, ${fieldLabel}: Regex validation is required for required fields`
                    );
                }
            }

            // mobileWithCode
            if (field.type === 'mobileWithCode' && field.required && !field.sub_key?.trim()) {
                errors.push(`Section ${compIndex + 1}, ${fieldLabel}: Sub Key is required for mobileWithCode fields`);
            }

            // File upload validation
            if (field.type === 'fileUpload' && field.required) {
                if (!field.maxFile || field.maxFile <= 0) {
                    errors.push(`Section ${compIndex + 1}, ${fieldLabel}: maxFile must be greater than 0`);
                }
                if (!field.fileSize || field.fileSize <= 0) {
                    errors.push(`Section ${compIndex + 1}, ${fieldLabel}: fileSize must be greater than 0`);
                }
                if (!field.fileExtension?.trim()) {
                    errors.push(`Section ${compIndex + 1}, ${fieldLabel}: fileExtension is required`);
                }
                if (!field.fileStoragePath?.trim()) {
                    errors.push(`Section ${compIndex + 1}, ${fieldLabel}: fileStoragePath is required`);
                }
            }
            if (field.type === 'agreementCheckbox' && field.agreementType === AgreementType.URL) {
                if (!field.placeholder?.trim()) {
                    errors.push(`Section ${compIndex + 1}, ${fieldLabel}: Link URL is required when agreement type is URL`);
                } else {
                    // URL validation
                    try {
                        new URL(field.placeholder);
                    } catch {
                        errors.push(`Section ${compIndex + 1}, ${fieldLabel}: Invalid URL format`);
                    }
                }
            }

            if (field.type === FieldTypes.OBJECT_LIST) {
                if (!field.columns?.length) {
                    errors.push(`Section ${compIndex + 1}, ${fieldLabel}: At least one object-list column is required`);
                } else {
                    field.columns.forEach((column, columnIndex) => {
                        const columnPrefix = `Section ${compIndex + 1}, ${fieldLabel}, Column ${columnIndex + 1}`;
                        if (!column.key?.trim()) errors.push(`${columnPrefix}: Object Key is required`);
                        if (!column.label?.trim()) errors.push(`${columnPrefix}: English heading is required`);
                        if (!column.label_ar?.trim()) errors.push(`${columnPrefix}: Arabic heading is required`);
                    });
                }
            }

            // Options validation
            if (OPTION_SUPPORTING_TYPES.has(field.type)) {
                if (!field.options || field.options.length === 0) {
                    errors.push(`Section ${compIndex + 1}, ${fieldLabel}: At least one option is required`);
                } else {
                    field.options.forEach((option, optIndex) => {
                        if (!option.label?.trim()) {
                            errors.push(
                                `Section ${compIndex + 1}, ${fieldLabel}, Option ${optIndex + 1}: Label (English) is required`
                            );
                        }
                        if (!option.label_ar?.trim()) {
                            errors.push(
                                `Section ${compIndex + 1}, ${fieldLabel}, Option ${optIndex + 1}: Label (Arabic) is required`
                            );
                        }
                        if (!option.value?.trim()) {
                            errors.push(
                                `Section ${compIndex + 1}, ${fieldLabel}, Option ${optIndex + 1}: Value is required`
                            );
                        }
                    });
                }
            }
        });
    });

    // Action validation
    if (pageData.action && pageData.action.length !== 0) {
        pageData.action.forEach((action, actionIndex) => {
            if (!action.label?.trim()) {
                errors.push(`Action ${actionIndex + 1}: Label (English) is required`);
            }
            if (!action.label_ar?.trim()) {
                errors.push(`Action ${actionIndex + 1}: Label (Arabic) is required`);
            }
            if (!action.key?.trim()) {
                errors.push(`Action ${actionIndex + 1}: Key is required`);
            }
            if (!action.type?.trim()) {
                errors.push(`Action ${actionIndex + 1}: Type is required`);
            }
        });
    }

    return {
        isValid: errors.length === 0,
        errors,
    };
};

// ==================== PAGE VARIABLES SECTION ====================

/**
 * Extracts variable placeholders from page configuration
 */
const extractVariables = (pageData: PageData): string[] => {
    const variableRegex = /\{\{(\w+)\}\}/g;
    const variables = new Set<string>();
    const fieldKeys = new Set<string>();


    // Extract variables excluding pageVariables itself
    const { pageVariables, ...dataToScan } = pageData;
    const pageDataString = JSON.stringify(dataToScan);
    const matches = pageDataString.matchAll(variableRegex);

    // Add variables excluding field keys
    for (const match of matches) {
        const varName = match[1];
        if (!fieldKeys.has(varName)) {
            variables.add(varName);
        }
    }

    return Array.from(variables);
};

/**
 * Page Variables Section Component
 * Displays and allows editing of detected page variables
 */
const PageVariablesSection = React.memo<{
    pageData: PageData;
    setPageData: React.Dispatch<React.SetStateAction<PageData>>;
}>(({ pageData, setPageData }) => {
    // Calculate variables excluding pageVariables from dependency
    const variables = useMemo(() => {
        const { pageVariables, ...rest } = pageData;
        return extractVariables(rest as PageData);
    }, [
        pageData.components,
        pageData.action,
        pageData.title,
        pageData.description,
        pageData.title_ar,
        pageData.description_ar,
    ]);

    // Initialize and clean up pageVariables
    useEffect(() => {
        if (variables.length > 0) {
            setPageData((prev) => {
                const newVars: Record<string, string> = {};

                // Only keep variables that still exist
                variables.forEach((varName) => {
                    newVars[varName] = prev.pageVariables?.[varName] || '';
                });

                // Check if we need to update
                const prevKeys = Object.keys(prev.pageVariables || {}).sort();
                const newKeys = Object.keys(newVars).sort();

                if (JSON.stringify(prevKeys) !== JSON.stringify(newKeys)) {
                    return { ...prev, pageVariables: newVars };
                }

                return prev;
            });
        } else {
            // No variables found, clear pageVariables
            setPageData((prev) => {
                if (prev.pageVariables && Object.keys(prev.pageVariables).length > 0) {
                    return { ...prev, pageVariables: {} };
                }
                return prev;
            });
        }
    }, [variables.join(','), setPageData]);

    const handleVariableChange = useCallback(
        (varName: string, value: string) => {
            setPageData((prev) => ({
                ...prev,
                pageVariables: {
                    ...prev.pageVariables,
                    [varName]: value,
                },
            }));
        },
        [setPageData]
    );

    if (variables.length === 0) {
        return null;
    }

    return (
        <Paper shadow="xs" p="md" mb="xl" withBorder>
            <Stack gap="md">
                <Text size="lg" fw={600} c="blue">
                    Page Variables ({variables.length})
                </Text>
                <Text size="sm" c="dimmed">
                    These variables are detected in your page configuration.
                </Text>

                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                    {variables.map((varName) => (
                        <TextInput
                            key={`${varName}-${variables.join(',')}`}
                            label={`{{${varName}}}`}
                            placeholder={`Enter value for ${varName}`}
                            defaultValue={pageData.pageVariables?.[varName] || ''}
                            onBlur={(e) => handleVariableChange(varName, e.currentTarget.value)}
                        />
                    ))}
                </SimpleGrid>
            </Stack>
        </Paper>
    );
});

PageVariablesSection.displayName = 'PageVariablesSection';

// ==================== MAIN COMPONENT ====================

/**
 * AddPageForm Component
 * 
 * Comprehensive form builder for creating and editing KYC pages with:
 * - Dynamic sections with configurable fields
 * - Support for multiple field types (text, input, select, radio, checkbox, etc.)
 * - Nested fields within options (conditional field display)
 * - Page actions (buttons, links, submit)
 * - Bilingual support (English/Arabic)
 * - Conditional visibility rules
 * - Variable substitution system
 * - File upload configuration
 * - JSON preview and validation
 * 
 * @component
 * @returns {JSX.Element} The page form builder interface
 */
const AddPageForm: React.FC = () => {
    // ==================== Hooks & State ====================
    const { pageID } = useParams<{ pageID?: string }>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const db = useFirestore();
    const returnPath = useMemo(
        () => getSafeReturnPath(searchParams.get('returnTo')),
        [searchParams]
    );
    const navigateBack = useCallback(() => {
        navigate(returnPath);
    }, [navigate, returnPath]);

    // Custom hooks
    const fieldTypes = useFieldTypes();
    const { expandedOptions, toggleExpanded } = useExpandedOptions();
    const { isLoading, isEditMode, pageData, setPageData } = useLoadPageData(pageID, db);

    // UI state
    const [isSaving, setIsSaving] = useState(false);

    // ==================== Helper Functions ====================

    /**
     * Gets field type configuration by type
     */
    const getFieldTypeConfig = useCallback(
        (type: FieldTypes): FieldTypeConfig | undefined => {
            return fieldTypes.find((ft) => ft.type === type);
        },
        [fieldTypes]
    );

    /**
     * Creates a default field based on field type
     */
    const createDefaultField = useCallback(
        (type: FieldTypes): SectionField => {
            const config = getFieldTypeConfig(type);

            if (!config) {
                return {
                    type,
                    unique_id: generateUniqueId(),
                    key: `${type}_${Date.now()}`,
                    is_visible_contract: true,
                    is_visible_kyc: true,
                    value: '',
                };
            }

            const field = {
                ...config.defaultTemplate,
                unique_id: generateUniqueId(),
                key: (config.defaultTemplate.key || type) + '_' + Date.now(),
                value: config.defaultTemplate.value ?? '',
                is_visible_contract: true,
                is_visible_kyc: true,
            } as SectionField;

            return field;
        },
        [getFieldTypeConfig]
    );


    // ==================== moveField HANDLERS ====================
    /**
     * Moves a field up in the list
     */
    const moveFieldUp = useCallback(
        (componentId: number, fieldIndex: number) => {
            if (fieldIndex === 0) return; // Already at top

            setPageData((prev) => ({
                ...prev,
                components: prev.components.map((comp) => {
                    if (comp.unique_id === componentId) {
                        const reorderedFields = [...comp.section_fields];
                        const [movedField] = reorderedFields.splice(fieldIndex, 1);
                        reorderedFields.splice(fieldIndex - 1, 0, movedField);

                        return {
                            ...comp,
                            section_fields: reorderedFields,
                        };
                    }
                    return comp;
                }),
            }));

            showNotification({
                color: 'blue',
                icon: <IconCheck />,
                message: 'Field moved up',
                autoClose: 1000,
            });
        },
        [setPageData]
    );

    /**
     * Moves a field down in the list
     */
    const moveFieldDown = useCallback(
        (componentId: number, fieldIndex: number, totalFields: number) => {
            if (fieldIndex === totalFields - 1) return; // Already at bottom

            setPageData((prev) => ({
                ...prev,
                components: prev.components.map((comp) => {
                    if (comp.unique_id === componentId) {
                        const reorderedFields = [...comp.section_fields];
                        const [movedField] = reorderedFields.splice(fieldIndex, 1);
                        reorderedFields.splice(fieldIndex + 1, 0, movedField);

                        return {
                            ...comp,
                            section_fields: reorderedFields,
                        };
                    }
                    return comp;
                }),
            }));

            showNotification({
                color: 'blue',
                icon: <IconCheck />,
                message: 'Field moved down',
                autoClose: 1000,
            });
        },
        [setPageData]
    );

    // ==================== Save Handler ====================

    /**
     * Saves the page configuration to Firestore
     */
    const handleSave = useCallback(async () => {
        if (!pageData.id || !pageData.title) {
            showNotification({
                color: 'red',
                icon: <IconCheck />,
                withBorder: true,
                message: 'Please fill in required fields (Page ID and Title)',
                autoClose: true,
            });
            return;
        }

        const validation = validatePageData(pageData, getFieldTypeConfig);

        if (!validation.isValid) {
            const errorMessage = validation.errors.join('<br/>');
            showNotification({
                color: 'red',
                icon: <IconExclamationCircle />,
                withBorder: true,
                message: <div dangerouslySetInnerHTML={{ __html: errorMessage }} />,
                autoClose: 10000,
            });
            console.error('Validation errors:', validation.errors);
            return;
        }

        setIsSaving(true);
        try {
            // Update eTag
            pageData.eTag = Timestamp.now().toMillis().toString();
            const cleanedData = cleanData(pageData);
            const docRef = doc(
                db,
                FIRESTORE_PATHS.CONFIG,
                FIRESTORE_PATHS.KYC_CONFIG,
                FIRESTORE_PATHS.KYC_PAGES,
                pageData.id
            );
            await setDoc(docRef, cleanedData, { merge: true });

            showNotification({
                color: 'teal',
                icon: <IconCheck />,
                withBorder: true,
                message: isEditMode ? 'Page updated successfully!' : 'Page created successfully!',
                autoClose: true,
            });

            setTimeout(() => {
                navigateBack();
            }, 1500);
        } catch (error) {
            console.error('Error saving page:', error);
            showNotification({
                color: 'red',
                icon: <IconCheck />,
                withBorder: true,
                message: 'Failed to save page. Please try again. ' + (error instanceof Error ? error.message : ''),
                autoClose: true,
            });
        } finally {
            setIsSaving(false);
        }
    }, [pageData, getFieldTypeConfig, isEditMode, db, navigateBack]);

    /**
     * Opens JSON preview modal
     */
    const openJsonPreview = useCallback(() => {
        modals.open({
            size: 'lg',
            title: 'JSON Preview',
            children: (
                <Box>
                    <JsonView src={pageData} theme="vscode" />
                </Box>
            ),
        });
    }, [pageData]);

    // ==================== Component Section Handlers ====================

    /**
     * Adds a new component section
     */
    const addComponentSection = useCallback(() => {
        const newSection: ComponentSection = {
            section_title: 'New Section',
            section_title_ar: 'مكون جديد',
            section_description: 'Section description',
            section_description_ar: 'وصف القسم',
            list_view: false,
            section_fields: [],
            is_visible_kyc: true,
            is_visible_contract: true,
            unique_id: generateUniqueId(),
        };

        setPageData((prev) => ({
            ...prev,
            components: [...prev.components, newSection],
        }));
    }, [setPageData]);

    /**
     * Removes a component section
     */
    const removeComponentSection = useCallback(
        (componentId: number) => {
            setPageData((prev) => ({
                ...prev,
                components: prev.components.filter((comp) => comp.unique_id !== componentId),
            }));
        },
        [setPageData]
    );

    /**
     * Moves a component section up in the page
     */
    const moveComponentSectionUp = useCallback(
        (sectionIndex: number) => {
            if (sectionIndex === 0) return;

            setPageData((prev) => {
                const reorderedComponents = [...prev.components];
                const [movedComponent] = reorderedComponents.splice(sectionIndex, 1);
                reorderedComponents.splice(sectionIndex - 1, 0, movedComponent);

                return {
                    ...prev,
                    components: reorderedComponents,
                };
            });

            showNotification({
                color: 'blue',
                icon: <IconCheck />,
                message: 'Section moved up',
                autoClose: 1000,
            });
        },
        [setPageData]
    );

    /**
     * Moves a component section down in the page
     */
    const moveComponentSectionDown = useCallback(
        (sectionIndex: number, totalSections: number) => {
            if (sectionIndex === totalSections - 1) return;

            setPageData((prev) => {
                const reorderedComponents = [...prev.components];
                const [movedComponent] = reorderedComponents.splice(sectionIndex, 1);
                reorderedComponents.splice(sectionIndex + 1, 0, movedComponent);

                return {
                    ...prev,
                    components: reorderedComponents,
                };
            });

            showNotification({
                color: 'blue',
                icon: <IconCheck />,
                message: 'Section moved down',
                autoClose: 1000,
            });
        },
        [setPageData]
    );

    /**
     * Updates a component section property
     */
    const updateComponentSection = useCallback(
        (componentId: number, field: keyof ComponentSection, value: any) => {
            setPageData((prev) => ({
                ...prev,
                components: prev.components.map((comp) =>
                    comp.unique_id === componentId ? { ...comp, [field]: value } : comp
                ),
            }));
        },
        [setPageData]
    );

    // ==================== Field Handlers ====================

    /**
     * Adds a field to a component section
     */
    const addFieldToComponent = useCallback(
        (componentId: number, fieldType: FieldTypes) => {
            const newField = createDefaultField(fieldType);

            setPageData((prev) => ({
                ...prev,
                components: prev.components.map((comp) =>
                    comp.unique_id === componentId
                        ? { ...comp, section_fields: [...comp.section_fields, newField] }
                        : comp
                ),
            }));
        },
        [createDefaultField, setPageData]
    );

    /**
     * Removes a field from a component section with confirmation
     */
    const removeFieldFromComponent = useCallback(
        (componentId: number, fieldId: number, fieldLabel?: string) => {
            modals.openConfirmModal({
                title: 'Delete Field',
                centered: true,
                children: (
                    <Text size="sm">
                        Are you sure you want to remove the field "{fieldLabel || 'Untitled Field'}"? This action
                        cannot be undone.
                    </Text>
                ),
                labels: { confirm: 'Delete Field', cancel: 'Cancel' },
                confirmProps: { color: 'red' },
                onConfirm: () => {
                    setPageData((prev) => ({
                        ...prev,
                        components: prev.components.map((comp) =>
                            comp.unique_id === componentId
                                ? {
                                    ...comp,
                                    section_fields: comp.section_fields.filter(
                                        (field) => field.unique_id !== fieldId
                                    ),
                                }
                                : comp
                        ),
                    }));
                },
            });
        },
        [setPageData]
    );

    /**
     * Updates a section field property
     */
    const updateSectionField = useCallback(
        (componentId: number, fieldId: number, field: keyof SectionField, value: any) => {
            setPageData((prev) => ({
                ...prev,
                components: prev.components.map((comp) => {
                    if (comp.unique_id === componentId) {
                        return {
                            ...comp,
                            section_fields: comp.section_fields.map((sectionField) => {
                                if (sectionField.unique_id === fieldId) {
                                    const updatedField = { ...sectionField, [field]: value };
                                    return updatedField;
                                }
                                return sectionField;
                            }),
                        };
                    }
                    return comp;
                }),
            }));
        },
        [setPageData]
    );

    // ==================== Option Handlers ====================

    /**
     * Adds an option to a field (select/radio/checkbox)
     */
    const addOptionToField = useCallback(
        (componentId: number, fieldId: number) => {
            setPageData((prev) => ({
                ...prev,
                components: prev.components.map((comp) => {
                    if (comp.unique_id === componentId) {
                        return {
                            ...comp,
                            section_fields: comp.section_fields.map((field) => {
                                if (field.unique_id === fieldId) {
                                    const currentOptions = field.options || [];
                                    return {
                                        ...field,
                                        options: [
                                            ...currentOptions,
                                            {
                                                label: 'New Option',
                                                label_ar: 'خيار جديد',
                                                value: 'new_option_' + Date.now(),
                                                section_fields: [],
                                                unique_id: generateUniqueId(),
                                            },
                                        ],
                                    };
                                }
                                return field;
                            }),
                        };
                    }
                    return comp;
                }),
            }));
        },
        [setPageData]
    );

    /**
     * Removes an option from a field with confirmation
     */
    const removeOptionFromField = useCallback(
        (componentId: number, fieldId: number, optionIndex: number, optionLabel?: string) => {
            modals.openConfirmModal({
                title: 'Delete Option',
                centered: true,
                children: (
                    <Text size="sm">
                        Are you sure you want to remove the option "{optionLabel || 'Untitled Option'}"? This
                        action cannot be undone.
                    </Text>
                ),
                labels: { confirm: 'Delete Option', cancel: 'Cancel' },
                confirmProps: { color: 'red' },
                onConfirm: () => {
                    setPageData((prev) => ({
                        ...prev,
                        components: prev.components.map((comp) => {
                            if (comp.unique_id === componentId) {
                                return {
                                    ...comp,
                                    section_fields: comp.section_fields.map((field) => {
                                        if (field.unique_id === fieldId && field.options) {
                                            return {
                                                ...field,
                                                options: field.options.filter((_, index) => index !== optionIndex),
                                            };
                                        }
                                        return field;
                                    }),
                                };
                            }
                            return comp;
                        }),
                    }));
                },
            });
        },
        [setPageData]
    );

    /**
     * Updates an option property
     */
    const updateFieldOption = useCallback(
        (
            componentId: number,
            fieldId: number,
            optionIndex: number,
            field: keyof ComponentOption,
            value: string
        ) => {
            setPageData((prev) => ({
                ...prev,
                components: prev.components.map((comp) => {
                    if (comp.unique_id === componentId) {
                        return {
                            ...comp,
                            section_fields: comp.section_fields.map((sectionField) => {
                                if (sectionField.unique_id === fieldId && sectionField.options) {
                                    const newOptions = [...sectionField.options];
                                    const updatedOption = { ...newOptions[optionIndex], [field]: value };

                                    newOptions[optionIndex] = updatedOption;
                                    return { ...sectionField, options: newOptions };
                                }
                                return sectionField;
                            }),
                        };
                    }
                    return comp;
                }),
            }));
        },
        [setPageData]
    );

    // ==================== Nested Field Handlers ====================

    /**
     * Adds a nested field to an option
     */
    const addNestedFieldToOption = useCallback(
        (
            componentId: number,
            fieldId: number,
            optionPath: number[],
            fieldType: FieldTypes = FieldTypes.INPUT
        ) => {
            const newNestedField = createDefaultField(fieldType);

            setPageData((prev) => {
                const newData = JSON.parse(JSON.stringify(prev));
                const compIndex = newData.components.findIndex((comp: any) => comp.unique_id === componentId);
                if (compIndex === -1) return prev;

                const fieldIndex = newData.components[compIndex].section_fields.findIndex(
                    (field: any) => field.unique_id === fieldId
                );
                if (fieldIndex === -1) return prev;

                let targetOption = newData.components[compIndex].section_fields[fieldIndex].options[optionPath[0]];

                for (let i = 1; i < optionPath.length; i++) {
                    if (!targetOption.section_fields) break;
                    const nestedField = targetOption.section_fields.find(
                        (field: any) => ['radio', 'checkbox'].includes(field.type) && field.options
                    );
                    if (!nestedField?.options) break;
                    targetOption = nestedField.options[optionPath[i]];
                }

                if (!targetOption.section_fields) targetOption.section_fields = [];
                targetOption.section_fields.push(newNestedField);
                return newData;
            });
        },
        [createDefaultField, setPageData]
    );

    /**
     * Removes a nested field from an option
     */
    const removeNestedFieldFromOption = useCallback(
        (componentId: number, fieldId: number, optionPath: number[], nestedFieldIndex: number) => {
            setPageData((prev) => {
                const newData = JSON.parse(JSON.stringify(prev));
                const compIndex = newData.components.findIndex((comp: any) => comp.unique_id === componentId);
                if (compIndex === -1) return prev;

                const fieldIndex = newData.components[compIndex].section_fields.findIndex(
                    (field: any) => field.unique_id === fieldId
                );
                if (fieldIndex === -1) return prev;

                let targetOption = newData.components[compIndex].section_fields[fieldIndex].options[optionPath[0]];

                for (let i = 1; i < optionPath.length; i++) {
                    if (!targetOption.section_fields) break;
                    const nestedField = targetOption.section_fields.find(
                        (field: any) => ['radio', 'checkbox'].includes(field.type) && field.options
                    );
                    if (!nestedField?.options) break;
                    targetOption = nestedField.options[optionPath[i]];
                }

                if (targetOption.section_fields) {
                    targetOption.section_fields = targetOption.section_fields.filter(
                        (_: any, idx: number) => idx !== nestedFieldIndex
                    );
                }
                return newData;
            });
        },
        [setPageData]
    );

    /**
     * Updates a nested field property
     */
    const updateNestedField = useCallback(
        (
            componentId: number,
            fieldId: number,
            optionPath: number[],
            nestedFieldIndex: number,
            property: keyof SectionField,
            value: any
        ) => {
            setPageData((prev) => {
                const newData = JSON.parse(JSON.stringify(prev));
                const compIndex = newData.components.findIndex((comp: any) => comp.unique_id === componentId);
                if (compIndex === -1) return prev;

                const fieldIndex = newData.components[compIndex].section_fields.findIndex(
                    (field: any) => field.unique_id === fieldId
                );
                if (fieldIndex === -1) return prev;

                let targetOption = newData.components[compIndex].section_fields[fieldIndex].options[optionPath[0]];

                for (let i = 1; i < optionPath.length; i++) {
                    if (!targetOption.section_fields) break;
                    const nestedField = targetOption.section_fields.find(
                        (f: any) => ['radio', 'checkbox'].includes(f.type) && f.options
                    );
                    if (!nestedField) break;
                    targetOption = nestedField.options[optionPath[i]];
                }

                if (targetOption.section_fields?.[nestedFieldIndex]) {
                    const updatedField = { ...targetOption.section_fields[nestedFieldIndex], [property]: value };
                    if (property === 'label' && value) {
                        updatedField.key = generateFieldKey(value);
                    }
                    targetOption.section_fields[nestedFieldIndex] = updatedField;
                }
                return newData;
            });
        },
        [setPageData]
    );

    /**
     * Adds a nested option to a nested field
     */
    const addNestedOptionToField = useCallback(
        (componentId: number, fieldId: number, optionPath: number[], nestedFieldIndex: number) => {
            setPageData((prev) => {
                const newData = JSON.parse(JSON.stringify(prev));
                const compIndex = newData.components.findIndex((comp: any) => comp.unique_id === componentId);
                if (compIndex === -1) return prev;

                const fieldIndex = newData.components[compIndex].section_fields.findIndex(
                    (field: any) => field.unique_id === fieldId
                );
                if (fieldIndex === -1) return prev;

                let targetOption = newData.components[compIndex].section_fields[fieldIndex].options[optionPath[0]];

                for (let i = 1; i < optionPath.length; i++) {
                    if (!targetOption.section_fields) break;
                    const nestedField = targetOption.section_fields.find(
                        (f: any) => ['radio', 'checkbox'].includes(f.type) && f.options
                    );
                    if (!nestedField) break;
                    targetOption = nestedField.options[optionPath[i]];
                }

                if (targetOption.section_fields?.[nestedFieldIndex]) {
                    const nestedField = targetOption.section_fields[nestedFieldIndex];
                    if (['radio', 'checkbox'].includes(nestedField.type)) {
                        if (!nestedField.options) nestedField.options = [];
                        nestedField.options.push({
                            label: 'New Nested Option',
                            label_ar: 'خيار متداخل جديد',
                            value: 'nested_option_' + Date.now(),
                            section_fields: [],
                            unique_id: generateUniqueId(),
                        });
                    }
                }
                return newData;
            });
        },
        [setPageData]
    );

    /**
     * Removes a nested option from a nested field
     */
    const removeNestedOptionFromField = useCallback(
        (
            componentId: number,
            fieldId: number,
            optionPath: number[],
            nestedFieldIndex: number,
            nestedOptionIndex: number
        ) => {
            setPageData((prev) => {
                const newData = JSON.parse(JSON.stringify(prev));
                const compIndex = newData.components.findIndex((comp: any) => comp.unique_id === componentId);
                if (compIndex === -1) return prev;

                const fieldIndex = newData.components[compIndex].section_fields.findIndex(
                    (field: any) => field.unique_id === fieldId
                );
                if (fieldIndex === -1) return prev;

                let targetOption = newData.components[compIndex].section_fields[fieldIndex].options[optionPath[0]];

                for (let i = 1; i < optionPath.length; i++) {
                    if (!targetOption.section_fields) break;
                    const nestedField = targetOption.section_fields.find(
                        (f: any) => ['radio', 'checkbox'].includes(f.type) && f.options
                    );
                    if (!nestedField) break;
                    targetOption = nestedField.options[optionPath[i]];
                }

                if (targetOption.section_fields?.[nestedFieldIndex]?.options) {
                    targetOption.section_fields[nestedFieldIndex].options = targetOption.section_fields[
                        nestedFieldIndex
                    ].options.filter((_: any, idx: number) => idx !== nestedOptionIndex);
                }
                return newData;
            });
        },
        [setPageData]
    );

    /**
     * Updates a nested option property
     */
    const updateNestedOption = useCallback(
        (
            componentId: number,
            fieldId: number,
            optionPath: number[],
            nestedFieldIndex: number,
            nestedOptionIndex: number,
            property: string,
            value: string
        ) => {
            setPageData((prev) => {
                const newData = JSON.parse(JSON.stringify(prev));
                const compIndex = newData.components.findIndex((c: any) => c.unique_id === componentId);
                if (compIndex === -1) return prev;

                const fieldIndex = newData.components[compIndex].section_fields.findIndex(
                    (f: any) => f.unique_id === fieldId
                );
                if (fieldIndex === -1) return prev;

                let targetOption = newData.components[compIndex].section_fields[fieldIndex].options[optionPath[0]];

                for (let i = 1; i < optionPath.length; i++) {
                    const nf = targetOption.section_fields?.find(
                        (f: any) => ['radio', 'checkbox'].includes(f.type) && f.options
                    );
                    if (nf) targetOption = nf.options[optionPath[i]];
                }

                if (targetOption.section_fields?.[nestedFieldIndex]?.options?.[nestedOptionIndex]) {
                    targetOption.section_fields[nestedFieldIndex].options[nestedOptionIndex][property] = value;
                    if (property === 'label') {
                        targetOption.section_fields[nestedFieldIndex].options[nestedOptionIndex].value =
                            generateFieldKey(value);
                    }
                }
                return newData;
            });
        },
        [setPageData]
    );

    // ==================== Action Handlers ====================

    /**
     * Adds a page action
     */
    const addPageAction = useCallback(() => {
        const newAction: PageAction = {
            type: 'button',
            label: 'New Action',
            label_ar: 'إجراء جديد',
            key: 'new_action_' + Date.now(),
            is_save: false,
            visible: true,
            unique_id: generateUniqueId(),
        };
        setPageData((prev) => ({ ...prev, action: [...prev.action, newAction] }));
    }, [setPageData]);

    /**
     * Removes a page action with confirmation
     */
    const removePageAction = useCallback(
        (actionId: number, actionLabel?: string) => {
            modals.openConfirmModal({
                title: 'Delete Action',
                centered: true,
                children: (
                    <Text size="sm">
                        Are you sure you want to remove the action "{actionLabel || 'Untitled Action'}"?
                    </Text>
                ),
                labels: { confirm: 'Delete Action', cancel: 'Cancel' },
                confirmProps: { color: 'red' },
                onConfirm: () =>
                    setPageData((prev) => ({
                        ...prev,
                        action: prev.action.filter((action) => action.unique_id !== actionId),
                    })),
            });
        },
        [setPageData]
    );

    /**
     * Updates a page action property
     */
    const updatePageAction = useCallback(
        (actionId: number, field: keyof PageAction, value: any) => {
            setPageData((prev) => ({
                ...prev,
                action: prev.action.map((action) => {
                    if (action.unique_id === actionId) {
                        const updatedAction = { ...action, [field]: value };
                        if (field === 'label' && value) {
                            updatedAction.key = generateFieldKey(value);
                        }
                        return updatedAction;
                    }
                    return action;
                }),
            }));
        },
        [setPageData]
    );

    // ==================== Render Helpers ====================

    /**
     * Recursively renders nested fields within options
     */
    const renderNestedField = useCallback(
        (
            nestedField: SectionField,
            componentId: number,
            fieldId: number,
            optionPath: number[],
            nestedFieldIndex: number,
            level: number = 1
        ): React.ReactNode => {
            const fieldConfig = getFieldTypeConfig(nestedField.type);

            return (
                <FieldEditor
                    key={`${optionPath.join('-')}-${nestedFieldIndex}`}
                    field={nestedField}
                    fieldIndex={nestedFieldIndex}
                    fieldTypes={fieldTypes}
                    level={level}
                    onUpdate={(property, value) =>
                        updateNestedField(componentId, fieldId, optionPath, nestedFieldIndex, property, value)
                    }
                    onRemove={() => removeNestedFieldFromOption(componentId, fieldId, optionPath, nestedFieldIndex)}
                >
                    {fieldConfig?.hasOptions && ['radio', 'checkbox'].includes(nestedField.type) && (
                        <>
                            <Divider my="sm" />
                            <Group justify="space-between" mb="sm">
                                <Text fw={500} size="sm">
                                    Options for Level {level}
                                </Text>
                                <Button
                                    size="xs"
                                    variant="light"
                                    onClick={() => addNestedOptionToField(componentId, fieldId, optionPath, nestedFieldIndex)}
                                >
                                    Add Option
                                </Button>
                            </Group>

                            {nestedField.options?.map((nestedOption, nestedOptionIndex) => {
                                const nestedOptionId = `${optionPath.join('-')}-${nestedFieldIndex}-${nestedOptionIndex}`;
                                const isNestedExpanded = expandedOptions.has(nestedOptionId);

                                return (
                                    <OptionEditor
                                        key={nestedOptionIndex}
                                        option={nestedOption}
                                        optionIndex={nestedOptionIndex}
                                        level={level}
                                        isExpanded={isNestedExpanded}
                                        fieldType={fieldConfig.type}
                                        onUpdate={(field, value) =>
                                            updateNestedOption(
                                                componentId,
                                                fieldId,
                                                optionPath,
                                                nestedFieldIndex,
                                                nestedOptionIndex,
                                                field,
                                                value
                                            )
                                        }
                                        onRemove={() =>
                                            removeNestedOptionFromField(
                                                componentId,
                                                fieldId,
                                                optionPath,
                                                nestedFieldIndex,
                                                nestedOptionIndex
                                            )
                                        }
                                        onToggleExpand={() => toggleExpanded(nestedOptionId)}
                                    >
                                        {isNestedExpanded && (
                                            <Card
                                                withBorder
                                                p="xs"
                                                mt="sm"
                                                style={{ backgroundColor: 'white' }}
                                            >
                                                <Group justify="space-between" mb="xs">
                                                    <Text size="xs" fw={500}>
                                                        Nested Fields for "{nestedOption.label}" (Level {level + 1})
                                                    </Text>
                                                    <FieldTypeSelector
                                                        fieldTypes={fieldTypes}
                                                        size="xs"
                                                        onSelect={(type) =>
                                                            addNestedFieldToOption(
                                                                componentId,
                                                                fieldId,
                                                                [...optionPath, nestedOptionIndex],
                                                                type
                                                            )
                                                        }
                                                        onQuickAdd={() =>
                                                            addNestedFieldToOption(
                                                                componentId,
                                                                fieldId,
                                                                [...optionPath, nestedOptionIndex],
                                                                FieldTypes.INPUT
                                                            )
                                                        }
                                                    />
                                                </Group>
                                                <Stack gap="xs">
                                                    {nestedOption.section_fields?.map((deepNestedField, deepNestedIndex) =>
                                                        renderNestedField(
                                                            deepNestedField,
                                                            componentId,
                                                            fieldId,
                                                            [...optionPath, nestedOptionIndex],
                                                            deepNestedIndex,
                                                            level + 1
                                                        )
                                                    )}
                                                </Stack>
                                                {(!nestedOption.section_fields || nestedOption.section_fields.length === 0) && (
                                                    <Text size="xs" c="dimmed" ta="center" p="xs">
                                                        No nested fields yet for level {level + 1}
                                                    </Text>
                                                )}
                                            </Card>
                                        )}
                                    </OptionEditor>
                                );
                            })}

                            {(!nestedField.options || nestedField.options.length === 0) && (
                                <Text size="xs" c="dimmed" ta="center" p="xs">
                                    No options yet. Add an option above.
                                </Text>
                            )}
                        </>
                    )}
                </FieldEditor>
            );
        },
        [
            fieldTypes,
            expandedOptions,
            getFieldTypeConfig,
            updateNestedField,
            removeNestedFieldFromOption,
            addNestedOptionToField,
            updateNestedOption,
            removeNestedOptionFromField,
            toggleExpanded,
            addNestedFieldToOption,
        ]
    );

    /**
     * Renders a section field editor
     */
    const renderSectionFieldEditor = useCallback(
        (field: SectionField, componentId: number, fieldIndex: number, totalFields: number) => {
            const fieldConfig = getFieldTypeConfig(field.type);

            return (
                <FieldEditor
                    key={field.unique_id}
                    field={field}
                    fieldIndex={fieldIndex}
                    fieldTypes={fieldTypes}
                    level={0}
                    moveable={totalFields > 1}
                    onUpdate={(property, value) => {
                        updateSectionField(componentId, field.unique_id!, property, value);
                    }}
                    onRemove={() =>
                        field.unique_id && removeFieldFromComponent(componentId, field.unique_id, field.label)
                    }
                    onMoveUp={() => moveFieldUp(componentId, fieldIndex)}
                    onMoveDown={() => moveFieldDown(componentId, fieldIndex, totalFields)}
                    isFirst={fieldIndex === 0}
                    isLast={fieldIndex === totalFields - 1}
                    onTypeChange={(newType) => {
                        if (field.unique_id) {
                            const newField = createDefaultField(newType);
                            if (field.label) newField.label = field.label;
                            if (field.key) newField.key = field.key;
                            if (field.label_ar) newField.label_ar = field.label_ar;
                            if (field.required !== undefined) newField.required = field.required;
                            setPageData((prev) => ({
                                ...prev,
                                components: prev.components.map((comp) => {
                                    if (comp.unique_id === componentId) {
                                        return {
                                            ...comp,
                                            section_fields: comp.section_fields.map((f) =>
                                                f.unique_id === field.unique_id ? { ...newField, id: field.unique_id } : f
                                            ),
                                        };
                                    }
                                    return comp;
                                }),
                            }));
                        }
                    }}
                >
                    {/* Render options for radio/checkbox fields */}
                    {fieldConfig?.hasOptions && ['radio', 'checkbox'].includes(field.type) && (
                        <>
                            <Divider my="sm" />
                            <Group justify="space-between" mb="sm">
                                <Text fw={500}>Options with Nested Fields Support</Text>
                                <Button
                                    size="xs"
                                    variant="light"
                                    leftSection={<IconPlus size={14} />}
                                    onClick={() => field.unique_id && addOptionToField(componentId, field.unique_id)}
                                >
                                    Add Option
                                </Button>
                            </Group>

                            {field.options?.map((option, optionIndex) => {
                                const optionId = `${componentId}-${field.unique_id}-${optionIndex}`;
                                const isExpanded = expandedOptions.has(optionId);

                                return (
                                    <OptionEditor
                                        key={optionIndex}
                                        option={option}
                                        optionIndex={optionIndex}
                                        level={0}
                                        isExpanded={isExpanded}
                                        fieldType={fieldConfig.type}
                                        onUpdate={(f, v) => updateFieldOption(componentId, field.unique_id!, optionIndex, f, v)}
                                        onRemove={() =>
                                            removeOptionFromField(componentId, field.unique_id!, optionIndex, option.label)
                                        }
                                        onToggleExpand={() => toggleExpanded(optionId)}
                                    >
                                        <Card
                                            withBorder
                                            p="xs"
                                            mt="sm"
                                            style={{ backgroundColor: '#e3f2fd', borderLeft: '3px solid #2196f3' }}
                                        >
                                            <Group justify="space-between" mb="sm">
                                                <Text fw={500} size="sm" c="blue">
                                                    Nested Fields for "{option.label}"
                                                </Text>
                                                <FieldTypeSelector
                                                    fieldTypes={fieldTypes}
                                                    size="xs"
                                                    onSelect={(type) =>
                                                        addNestedFieldToOption(componentId, field.unique_id!, [optionIndex], type)
                                                    }
                                                    onQuickAdd={() =>
                                                        addNestedFieldToOption(
                                                            componentId,
                                                            field.unique_id!,
                                                            [optionIndex],
                                                            FieldTypes.INPUT
                                                        )
                                                    }
                                                />
                                            </Group>
                                            <Stack gap="xs">
                                                {option.section_fields?.map((nestedField, nestedFieldIndex) =>
                                                    renderNestedField(
                                                        nestedField,
                                                        componentId,
                                                        field.unique_id!,
                                                        [optionIndex],
                                                        nestedFieldIndex,
                                                        1
                                                    )
                                                )}
                                            </Stack>
                                            {(!option.section_fields || option.section_fields.length === 0) && (
                                                <Card
                                                    withBorder
                                                    p="md"
                                                    style={{ backgroundColor: 'white', textAlign: 'center' }}
                                                >
                                                    <Text size="sm" c="dimmed">
                                                        No nested fields yet. Select a field type above.
                                                    </Text>
                                                </Card>
                                            )}
                                        </Card>
                                    </OptionEditor>
                                );
                            })}
                        </>
                    )}

                    {/* Render options for select fields */}
                    {fieldConfig?.hasOptions && field.type === 'select' && (
                        <>
                            <Divider my="sm" />
                            <Group justify="space-between" mb="sm">
                                <Text fw={500}>Options</Text>
                                <Button
                                    size="xs"
                                    variant="light"
                                    leftSection={<IconPlus size={14} />}
                                    onClick={() => field.unique_id && addOptionToField(componentId, field.unique_id)}
                                >
                                    Add Option
                                </Button>
                            </Group>
                            {field.options?.map((option, optionIndex) => (
                                <Card key={optionIndex} withBorder p="sm" mb="xs">
                                    <Grid>
                                        <BilingualInput
                                            labelEn="Option Label (English)"
                                            labelAr="Option Label (Arabic)"
                                            valueEn={option.label}
                                            valueAr={option.label_ar}
                                            onChangeEn={(v) =>
                                                updateFieldOption(componentId, field.unique_id!, optionIndex, 'label', v)
                                            }
                                            onChangeAr={(v) =>
                                                updateFieldOption(componentId, field.unique_id!, optionIndex, 'label_ar', v)
                                            }
                                            required
                                            span={{ base: 12, sm: 6, lg: 4 }}
                                        />
                                        <Grid.Col span={{ base: 9, sm: 8, lg: 3 }}>
                                            <TextInput
                                                label="Option Value"
                                                value={option.value}
                                                onChange={(e) =>
                                                    updateFieldOption(componentId, field.unique_id!, optionIndex, 'value', e.target.value)
                                                }
                                            />
                                        </Grid.Col>
                                        <Grid.Col span={{ base: 3, sm: 4, lg: 1 }}>
                                            <Text size="sm" mb="xs">
                                                Remove
                                            </Text>
                                            <ActionIcon
                                                variant="light"
                                                color="red"
                                                onClick={() =>
                                                    removeOptionFromField(componentId, field.unique_id!, optionIndex, option.label)
                                                }
                                                aria-label="Remove option"
                                            >
                                                <IconTrash size={14} />
                                            </ActionIcon>
                                        </Grid.Col>
                                        <Grid.Col span={12}>
                                            <ConditionField
                                                label="On Select JS (Optional)"
                                                value={option.on_select || ''}
                                                onChange={(value) =>
                                                    updateFieldOption(
                                                        componentId,
                                                        field.unique_id!,
                                                        optionIndex,
                                                        'on_select',
                                                        value
                                                    )
                                                }
                                                onRemove={() =>
                                                    updateFieldOption(
                                                        componentId,
                                                        field.unique_id!,
                                                        optionIndex,
                                                        'on_select',
                                                        ''
                                                    )
                                                }
                                                placeholder="formData.skip_page_tags = ['kyc-form'];"
                                                conditionFalseLabel="No form data changes"
                                                conditionTrueLabel="Form data changed"
                                                editorMode="mutation"
                                                modalSubtitle="Configure your option selection script"
                                                saveLabel="Save Script"
                                            />
                                        </Grid.Col>
                                    </Grid>
                                </Card>
                            ))}
                        </>
                    )}
                </FieldEditor>
            );
        },
        [
            fieldTypes,
            getFieldTypeConfig,
            updateSectionField,
            removeFieldFromComponent,
            createDefaultField,
            setPageData,
            addOptionToField,
            expandedOptions,
            toggleExpanded,
            updateFieldOption,
            removeOptionFromField,
            addNestedFieldToOption,
            renderNestedField,
            moveFieldUp,
            moveFieldDown,
        ]
    );

    // ==================== Main Render ====================

    if (isLoading) {
        return (
            <main style={{ padding: '1rem 0' }}>
                <Text ta="center" size="lg">
                    Loading form configuration...
                </Text>
            </main>
        );
    }

    return (
        <main style={{ padding: '1rem 0' }}>
            {/* Page header */}
            <Group mb="xl">
                <ActionIcon onClick={navigateBack} aria-label="Back to previous KYC screen">
                    <IconArrowLeft size="1.5rem" />
                </ActionIcon>
                <Title>{isEditMode ? 'Edit Page' : 'Add Page'}</Title>
            </Group>

            <Space h="xl" />

            <FormCardWrapper gridProps={{ style: { width: '100%' } }} justify="center">
                {/* Page information section */}
                <BilingualInput
                    labelEn="Page Title"
                    labelAr="Page Title (Arabic)"
                    valueEn={pageData.title}
                    valueAr={pageData.title_ar}
                    onChangeEn={(v) => setPageData((prev) => ({ ...prev, title: v }))}
                    onChangeAr={(v) => setPageData((prev) => ({ ...prev, title_ar: v }))}
                    required
                    span={{ base: 12, lg: 6 }}
                />
                <BilingualTextarea
                    labelEn="Page Description"
                    labelAr="Page Description (Arabic)"
                    valueEn={pageData.description}
                    valueAr={pageData.description_ar}
                    onChangeEn={(v) => setPageData((prev) => ({ ...prev, description: v }))}
                    onChangeAr={(v) => setPageData((prev) => ({ ...prev, description_ar: v }))}
                />
                <FieldInput
                    label="Page ID"
                    value={pageData.id}
                    onChange={(v) => setPageData((prev) => ({ ...prev, id: v }))}
                    placeholder="kyc_requirements"
                    required
                    disabled={isEditMode}
                    span={{ base: 12, lg: 6 }}
                />
                <Grid.Col span={{ base: 12, lg: 6 }}>
                    <ConditionField
                        label="Page Condition (Optional)"
                        value={pageData.page_condition || ''}
                        onChange={(v) => setPageData((prev) => ({ ...prev, page_condition: v }))}
                        onRemove={() => setPageData((prev) => ({ ...prev, page_condition: '' }))}
                        conditionFalseLabel="This Page is user can shown"
                        conditionTrueLabel="This Page is skip "
                    />
                </Grid.Col>

                <Grid.Col span={{ base: 12, lg: 12 }}>
                    <TagsInput
                        label="PDF Tags"
                        placeholder="Type and press Enter"
                        value={pageData.tags || []}
                        onChange={(tags) => setPageData((prev) => ({ ...prev, tags }))}
                        clearable
                        splitChars={[',', ' ', '|']}
                    />
                </Grid.Col>

                <Grid.Col span={{ base: 12, lg: 12 }}>
                    <Switch
                        label="Start on a new PDF page"
                        description="In the contract PDF, this page's content begins on a fresh physical page instead of continuing after the previous content"
                        checked={pageData.pdf_start_on_new_page || false}
                        onChange={(e) =>
                            setPageData((prev) => ({
                                ...prev,
                                pdf_start_on_new_page: e.currentTarget.checked,
                            }))
                        }
                    />
                </Grid.Col>

                {/* Components section */}
                <Grid.Col span={12}>
                    <Divider my="md" />
                    <Group justify="space-between" mb="md">
                        <Title order={4}>Page Components</Title>
                        <Button leftSection={<IconPlus />} onClick={addComponentSection}>
                            Add Section
                        </Button>
                    </Group>

                    <Stack gap="lg">
                        {pageData.components.map((component, sectionIndex) => (
                            <Card
                                key={component.unique_id}
                                withBorder
                                p="md"
                                style={{ backgroundColor: component.background_color || '#f8f9fa' }}
                            >
                                <Group justify="space-between" mb="md">
                                    <Title order={5}>Section {sectionIndex + 1}</Title>
                                    {pageData.components.length > 1 && (
                                        <Group gap="xs">
                                            <ActionIcon
                                                variant="light"
                                                color="blue"
                                                disabled={sectionIndex === 0}
                                                onClick={() => moveComponentSectionUp(sectionIndex)}
                                                aria-label="Move section up"
                                            >
                                                <IconArrowUp size={16} />
                                            </ActionIcon>
                                            <ActionIcon
                                                variant="light"
                                                color="blue"
                                                disabled={sectionIndex === pageData.components.length - 1}
                                                onClick={() => moveComponentSectionDown(sectionIndex, pageData.components.length)}
                                                aria-label="Move section down"
                                            >
                                                <IconArrowDown size={16} />
                                            </ActionIcon>
                                        </Group>
                                    )}
                                </Group>

                                {/* Section information card */}
                                <Card withBorder p="md" mb="md" style={{ backgroundColor: 'white' }}>
                                    <Group justify="space-between" align="center" mb="md">
                                        <Text fw={500}>Section Information</Text>
                                        <Group gap="md">
                                            <BackgroundColorPickerDialog
                                                background_color={component.background_color}
                                                onChange={(color) =>
                                                    component.unique_id &&
                                                    updateComponentSection(component.unique_id, 'background_color', color)
                                                }
                                            />
                                            <Switch
                                                label="Visible on App"
                                                checked={component.is_visible_kyc}
                                                onChange={(e) =>
                                                    component.unique_id &&
                                                    updateComponentSection(component.unique_id, 'is_visible_kyc', e.currentTarget.checked)
                                                }
                                            />
                                            <Switch
                                                label="Visible on PDF"
                                                checked={component.is_visible_contract}
                                                onChange={(e) =>
                                                    component.unique_id &&
                                                    updateComponentSection(component.unique_id, 'is_visible_contract', e.currentTarget.checked)
                                                }
                                            />
                                            <Switch
                                                label="Visible Title"
                                                checked={component.is_visible_title}
                                                onChange={(e) =>
                                                    component.unique_id &&
                                                    updateComponentSection(component.unique_id, 'is_visible_title', e.currentTarget.checked)
                                                }
                                            />
                                            <Switch
                                                label="List View"
                                                checked={component.list_view}
                                                onChange={(e) =>
                                                    component.unique_id &&
                                                    updateComponentSection(component.unique_id, 'list_view', e.currentTarget.checked)
                                                }
                                            />
                                            <ActionIcon
                                                variant="light"
                                                color="red"
                                                onClick={() => {
                                                    if (!component.unique_id) return;
                                                    modals.openConfirmModal({
                                                        title: 'Delete section',
                                                        centered: true,
                                                        children: <Text size="sm">Are you sure you want to remove this section?</Text>,
                                                        labels: { confirm: 'Delete', cancel: 'Cancel' },
                                                        confirmProps: { color: 'red' },
                                                        onConfirm: () => removeComponentSection(component.unique_id!),
                                                    });
                                                }}
                                                aria-label="Remove section"
                                            >
                                                <IconTrash />
                                            </ActionIcon>
                                        </Group>
                                    </Group>

                                    <Grid>
                                        <BilingualInput
                                            labelEn="Section Title (English)"
                                            labelAr="Section Title (Arabic)"
                                            valueEn={component.section_title}
                                            valueAr={component.section_title_ar}
                                            required={component.is_visible_title ?? false}
                                            onChangeEn={(v) =>
                                                component.unique_id && updateComponentSection(component.unique_id, 'section_title', v)
                                            }
                                            onChangeAr={(v) =>
                                                component.unique_id && updateComponentSection(component.unique_id, 'section_title_ar', v)
                                            }
                                            span={{ base: 12, sm: 6, lg: 6 }}
                                        />
                                        <BilingualTextarea
                                            labelEn="Section Description (English)"
                                            labelAr="Section Description (Arabic)"
                                            valueEn={component.section_description}
                                            valueAr={component.section_description_ar}
                                            onChangeEn={(v) =>
                                                component.unique_id &&
                                                updateComponentSection(component.unique_id, 'section_description', v)
                                            }
                                            onChangeAr={(v) =>
                                                component.unique_id &&
                                                updateComponentSection(component.unique_id, 'section_description_ar', v)
                                            }
                                        />
                                    </Grid>
                                    <Grid>
                                        <Grid.Col span={{ base: 12, sm: 6 }}>
                                            <FieldInput
                                                label="Section ID"
                                                value={component.section_id || ''}
                                                onChange={(value) =>
                                                    component.unique_id && updateComponentSection(component.unique_id, 'section_id', value)
                                                }
                                                required
                                                span={{ base: 12 }}
                                                placeholder="kyc_section_1"
                                            />
                                        </Grid.Col>
                                        <Grid.Col span={{ base: 12, sm: 6 }}>
                                            <ConditionField
                                                label="Section Condition (Optional)"
                                                value={component.condition || ''}
                                                onChange={(value) =>
                                                    component.unique_id && updateComponentSection(component.unique_id, 'condition', value)
                                                }
                                                onRemove={() =>
                                                    component.unique_id && updateComponentSection(component.unique_id, 'condition', '')
                                                }
                                                conditionFalseLabel="This Section is visible mode"
                                                conditionTrueLabel="This Section is hidden mode"
                                            />
                                        </Grid.Col>
                                    </Grid>
                                </Card>

                                <Divider my="md" />

                                {/* Section fields */}
                                <Group justify="space-between" mb="sm">
                                    <Text fw={500}>Section Fields</Text>
                                    <FieldTypeSelector
                                        fieldTypes={fieldTypes}
                                        onSelect={(type) => component.unique_id && addFieldToComponent(component.unique_id, type)}
                                        onQuickAdd={() => component.unique_id && addFieldToComponent(component.unique_id, FieldTypes.INPUT)}
                                    />
                                </Group>

                                {component.section_fields.map((field, fieldIndex) =>
                                    renderSectionFieldEditor(field, component.unique_id!, fieldIndex, component.section_fields.length)
                                )}

                                {component.section_fields.length === 0 && (
                                    <Card withBorder p="sm" ta="center" c="dimmed">
                                        <Text size="sm">No fields added yet. Select a field type from the dropdown above.</Text>
                                    </Card>
                                )}
                            </Card>
                        ))}

                        {pageData.components.length === 0 && (
                            <Card withBorder p="xl" ta="center">
                                <Text c="dimmed">No component sections added yet. Click "Add Section" to get started.</Text>
                            </Card>
                        )}
                    </Stack>

                    <Divider my="xl" />

                    {/* Actions section */}
                    <Group justify="space-between" mb="md">
                        <Title order={4}>Page Actions</Title>
                        <Button leftSection={<IconPlus />} onClick={addPageAction}>
                            Add Action
                        </Button>
                    </Group>

                    <Card withBorder p="md">
                        <Stack gap="md">
                            {pageData.action.map((action) => (
                                <ActionEditor
                                    key={action.unique_id}
                                    action={action}
                                    onUpdate={(field, value) => action.unique_id && updatePageAction(action.unique_id, field, value)}
                                    onRemove={() => action.unique_id && removePageAction(action.unique_id, action.label)}
                                />
                            ))}
                        </Stack>
                    </Card>

                    {/* Page Variables Section */}
                    <PageVariablesSection pageData={pageData} setPageData={setPageData} />

                    <Divider my="xl" />

                    {/* Action buttons */}
                    <Group justify="center" gap="md">
                        <Button variant="default" onClick={navigateBack}>
                            Cancel
                        </Button>
                        <Button leftSection={<IconCheck />} onClick={handleSave} loading={isSaving}>
                            {isEditMode ? 'Update Page' : 'Create Page'}
                        </Button>
                        {(pageData.components.length > 0 || pageData.action.length > 1) && (
                            <Button color="grape" rightSection={<IconEye />} onClick={openJsonPreview}>
                                JSON Preview
                            </Button>
                        )}
                    </Group>
                </Grid.Col>
            </FormCardWrapper>
        </main>
    );
};

export default AddPageForm;
